"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  addToContentQueue,
  captureThought,
  classifyCapture,
  commitCapture,
  createCapture,
  createDecision,
  createScheduleBlock,
  createTask,
  generateSchedule,
  getGoalDraft,
  getMorningBrief,
  getPlanningOverview,
  getSchedule,
  getTaskBreakdown,
  interpretHomeInput,
  listGoals,
  matchTextToGoal,
  listTasks,
  previewContentCapture,
  routeCapture,
  completeScheduleBlock,
  summarizeQueueItem,
  updateTask,
  type CaptureCommitState,
  type CaptureCommitConfirmation,
  type CaptureResponse,
  type CaptureRouteProposal,
  type CaptureRouteState,
  type MorningBrief as EveningBrief,
  type GoalDraft,
  type HomeInterpretation,
  type HomeRelatedGoal,
  type LifeAllocation,
  type GoalItem,
  type ScheduleBlock,
  type SummarizeResponse,
  type TaskItem,
  type TaskBreakdown,
  type ThoughtClusterRef,
  type ThoughtCompression,
} from "@/lib/api";
import { makeRestartSmaller, moveRestartLater } from "@/lib/api/today";
import type { TodayPlacement } from "@/lib/api/projection-types";
import { getSettings } from "@/lib/settings-api";
import { invalidateProjectionStores } from "@/lib/stores/projectionInvalidation";
import { applyFreshTodayRestartFallback } from "@/lib/todayRestartFallback";
import { formatLocalDateInput, formatTime, getGreeting, parseUTCishDate } from "@/lib/utils";
import { describeGoalDraftExecutionStyle } from "@/lib/goalCadence";
import {
  useAuth,
  useCarryForwardContext,
  useDataRefresh,
  usePendingCaptureIntent,
} from "@/lib/store";
import {
  useCrossSurfaceConfirmationStore,
  type CrossSurfaceConfirmationStateKind,
  type CrossSurfaceRevealTarget,
  type CrossSurfaceConfirmationTruth,
} from "@/lib/stores/crossSurfaceConfirmation";
import { useTodayProjection } from "@/hooks/useTodayProjection";
import { useChatRevealHighlight } from "@/lib/useChatRevealHighlight";
// ConsistencyPulse + DeferredModulesRail removed in PR 5 (FR-12, AC-12, §6.3).
// Rhythm content moved to Reflect's "How are the rhythms?" section.
import { completeFocusItem } from "./completeFocus";
import { skipFocusItem } from "./skipFocus";
import RestartBlock from "./RestartBlock";
import EmptyStatePlanDayCta from "./EmptyStatePlanDayCta";
import QuickCaptureBar from "./QuickCaptureBar";
import CaptureResultPopup from "./CaptureResultPopup";
import QueuePeek from "./QueuePeek";
// ExploreCard moved to Reflect's "What's worth considering?" section (§6.3).
import EveningWrapPopup from "@/components/EveningWrapPopup";
import EveningRecapCard from "@/components/EveningRecapCard";
import MorningBriefPopup from "@/components/MorningBriefPopup";
import { useOverlayManager } from "@/components/overlays/OverlayManager";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";

const CAPTURE_TIMEOUT_MS = 20000;
const MORNING_BRIEF_IDLE_DELAY_MS = 1200;

type CaptureRouting = {
  sourceText: string;
  interpretation: HomeInterpretation | null;
  goalDraft: GoalDraft | null;
  taskBreakdown: TaskBreakdown | null;
  relatedGoal: HomeRelatedGoal | null;
};

type CaptureResultState = {
  captureId: string | null;
  clarifiedId: string | null;
  category: string;
  thoughtType: string | null;
  categoryConfidence: number;
  tags: string[];
  mood: string | null;
  moodConfidence: number;
  emotionalWeight: number;
  ambiguityScore: number;
  compression: ThoughtCompression | null;
  action: string | null;
  cluster: ThoughtClusterRef | null;
  agentMessage: string;
  sourceText: string;
  interpretation: HomeInterpretation | null;
  goalDraft: GoalDraft | null;
  taskBreakdown: TaskBreakdown | null;
  relatedGoal: HomeRelatedGoal | null;
  routeProposals: CaptureRouteProposal[];
  recommendedRouteKind: string | null;
  previewTitle?: string | null;
  isError?: boolean;
  userCorrected?: boolean;
};

type FeedbackBannerState = {
  token: number;
  message: string;
  ctaLabel?: string;
  onCta?: (() => void) | null;
};

type StartHereFocus = {
  id: string | null;
  title: string;
  why_now: string;
  confidence: "high" | "medium";
  effort_minutes?: number | null;
  goal?: {
    id: string;
    title: string;
  } | null;
  source_label: string;
  timing_label?: string | null;
  // Active-block flow: when the focus came from a schedule block
  // currently in progress, this is the remaining minutes of the
  // window so RestartBlock can auto-start its timer with the right
  // duration. ``auto_complete_at_end`` flips the timer's "done"
  // transition into auto-firing onComplete so the task closes when
  // the scheduled end_time arrives.
  auto_start_minutes?: number | null;
  auto_complete_at_end?: boolean;
};

type UserDayPhase = "morning" | "evening";
type CaptureScheduleIntent = {
  targetDate: string;
  dayLabel: string;
  durationMinutes: number;
  preferredStartHour: number;
  preferredEndHour: number;
  idealStartHour: number;
};

type QueueContentPreview = {
  title: string;
  content_type: string | null;
  duration_min?: number | null;
  provider?: string | null;
};

interface Props {
  onNavigate: (tab: string, initialMessage?: string) => void;
  isFirstSessionPending?: boolean;
  onFirstSessionComplete?: () => void;
}

const URL_CAPTURE_PATTERN = /https?:\/\/\S+/i;
const DECISION_SIGNAL_PATTERN =
  /^(should i|should we|do i|do we|would it be better|is it worth|can i|could i|which\b|whether\b)/i;
const SCHEDULE_FOCUS_KINDS = new Set<ScheduleBlock["kind"]>(["task", "custom"]);
const GOAL_LINK_STOP_WORDS = new Set([
  "about",
  "after",
  "before",
  "could",
  "every",
  "first",
  "focus",
  "from",
  "have",
  "into",
  "just",
  "make",
  "need",
  "next",
  "that",
  "this",
  "want",
  "with",
  "your",
]);
const GOAL_UPDATE_CAPTURE_PATTERN =
  /\b(my first|my next|first [a-z\s-]{0,24}\bsession|next [a-z\s-]{0,24}\bsession|put it on|move it to|schedule it|on (monday|tuesday|wednesday|thursday|friday|saturday|sunday)|\d{1,3}\s*(minutes?|mins?|min))\b/i;
const WEEKDAY_INDEXES: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};
const WEEKDAY_PATTERN = /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/;

function cleanCaptureMessage(message: string, action?: string | null): string {
  let cleaned = message
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/\s*with tags:\s*\[[^\]]*\]\.?/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  if (action) {
    cleaned = cleaned.replace(/Next step I spotted:\s*.*$/i, "").trim();
  }

  return cleaned;
}

async function promiseWithTimeout<T>(promise: Promise<T>) {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error("Capture is taking too long. Please try again.")), CAPTURE_TIMEOUT_MS);
  });

  return Promise.race([promise, timeoutPromise]);
}

function buildTargetDate(targetWeeks: number): string {
  const date = new Date();
  date.setDate(date.getDate() + Math.max(targetWeeks, 1) * 7);
  return date.toISOString();
}

function normalizeTaskTitle(text: string): string {
  const cleaned = text
    .trim()
    .replace(/^i\s+(need to|want to|wanna|have to|should)\s+/i, "")
    .replace(/^remember to\s+/i, "")
    .replace(/^todo:?\s*/i, "")
    .replace(/[.?!]+$/, "")
    .trim();

  if (!cleaned) return "New task";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function inferTaskMeta(text: string): {
  estimated_minutes: number;
  priority: number;
  energy_level: "low" | "medium" | "high";
} {
  const normalized = text.toLowerCase();

  if (/\b(email|reply|call|send|book|message|follow up)\b/.test(normalized)) {
    return { estimated_minutes: 20, priority: 2, energy_level: "low" };
  }

  if (/\b(study|learn|research|design|write|build|code|prepare|practice)\b/.test(normalized)) {
    return {
      estimated_minutes: 45,
      priority: /\b(today|urgent|asap|now|tomorrow|before lunch)\b/.test(normalized) ? 3 : 2,
      energy_level: "high",
    };
  }

  return {
    estimated_minutes: 30,
    priority: /\b(today|urgent|asap|now|tomorrow|before lunch)\b/.test(normalized) ? 3 : 2,
    energy_level: "medium",
  };
}

function roundUpToFiveMinutes(value: Date): Date {
  const next = new Date(value);
  next.setSeconds(0, 0);
  const remainder = next.getMinutes() % 5;
  if (remainder !== 0) {
    next.setMinutes(next.getMinutes() + (5 - remainder));
  }
  return next;
}

function inferCaptureScheduleIntent(
  text: string,
  interpretation: HomeInterpretation | null,
  fallbackMinutes: number
): CaptureScheduleIntent | null {
  const normalized = text.trim().toLowerCase();
  if (!normalized) return null;

  const weekdayMatch = normalized.match(WEEKDAY_PATTERN);
  const looksSchedulable =
    interpretation?.primary_type === "daily_plan" ||
    /\b(block|schedule|timebox|calendar)\b/.test(normalized) ||
    /\b(put it on|put on|move it to|move to)\b/.test(normalized) ||
    Boolean(weekdayMatch && /\b(session|block|minutes?|mins?|min|first|next)\b/.test(normalized));
  if (!looksSchedulable) return null;

  const mentionsDateOrDaypart =
    /\b(today|tomorrow|tonight|morning|afternoon|evening)\b/.test(normalized) ||
    Boolean(weekdayMatch);
  if (!mentionsDateOrDaypart) return null;

  const durationMatch = normalized.match(/\b(\d{1,3})\s*(?:minutes?|mins?|min)\b/);
  const durationMinutes = durationMatch
    ? Math.max(10, Math.min(Number(durationMatch[1]), 180))
    : Math.max(fallbackMinutes || 30, 10);

  const target = new Date();
  let dayBaseLabel = "today";
  if (/\btomorrow\b/.test(normalized)) {
    target.setDate(target.getDate() + 1);
    dayBaseLabel = "tomorrow";
  } else if (weekdayMatch) {
    const weekday = weekdayMatch[1];
    const targetDay = WEEKDAY_INDEXES[weekday];
    const daysUntil = ((targetDay - target.getDay()) + 7) % 7 || 7;
    target.setDate(target.getDate() + daysUntil);
    dayBaseLabel = weekday;
  }

  let preferredStartHour = 9;
  let preferredEndHour = 18;
  let idealStartHour = 11;
  let daypartLabel = "";

  if (/\btonight\b/.test(normalized) || /\bevening\b/.test(normalized)) {
    preferredStartHour = 17;
    preferredEndHour = 21;
    idealStartHour = 18;
    daypartLabel = "evening";
  } else if (/\bafternoon\b/.test(normalized)) {
    preferredStartHour = 13;
    preferredEndHour = 17;
    idealStartHour = 14;
    daypartLabel = "afternoon";
  } else if (/\bmorning\b/.test(normalized)) {
    preferredStartHour = 8;
    preferredEndHour = 12;
    idealStartHour = 9;
    daypartLabel = "morning";
  }

  return {
    targetDate: formatLocalDateInput(target),
    dayLabel: `${dayBaseLabel}${daypartLabel ? ` ${daypartLabel}` : ""}`.trim(),
    durationMinutes,
    preferredStartHour,
    preferredEndHour,
    idealStartHour,
  };
}

function scorePreferredScheduleStart(start: Date, idealStartHour: number): number {
  const hourValue = start.getHours() + start.getMinutes() / 60;
  return -Math.abs(hourValue - idealStartHour);
}

function findPreferredScheduleSlot(
  blocks: Array<{ start_time: string; end_time: string }>,
  intent: CaptureScheduleIntent
): { start: Date; end: Date } | null {
  const dayStart = new Date(
    `${intent.targetDate}T${String(intent.preferredStartHour).padStart(2, "0")}:00:00`
  );
  const dayEnd = new Date(
    `${intent.targetDate}T${String(intent.preferredEndHour).padStart(2, "0")}:00:00`
  );
  const now = roundUpToFiveMinutes(new Date());
  let cursor =
    formatLocalDateInput(now) === intent.targetDate && now > dayStart ? now : dayStart;

  const orderedBlocks = [...blocks]
    .map((block) => ({
      start: parseUTCishDate(block.start_time),
      end: parseUTCishDate(block.end_time),
    }))
    .filter((block) => block.end > dayStart && block.start < dayEnd)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const candidateSlots: Array<{ start: Date; end: Date; score: number }> = [];

  const pushCandidate = (start: Date, endBoundary: Date) => {
    const gapMinutes = Math.round((endBoundary.getTime() - start.getTime()) / 60000);
    if (gapMinutes < intent.durationMinutes) return;
    candidateSlots.push({
      start,
      end: new Date(start.getTime() + intent.durationMinutes * 60000),
      score: scorePreferredScheduleStart(start, intent.idealStartHour),
    });
  };

  for (const block of orderedBlocks) {
    if (block.end <= cursor) continue;
    if (block.start > cursor) {
      pushCandidate(cursor, block.start < dayEnd ? block.start : dayEnd);
    }
    if (block.end >= dayEnd) {
      cursor = dayEnd;
      break;
    }
    cursor = block.end > cursor ? block.end : cursor;
  }

  if (cursor < dayEnd) {
    pushCandidate(cursor, dayEnd);
  }

  if (candidateSlots.length === 0) return null;
  candidateSlots.sort((left, right) => {
    if (right.score !== left.score) return right.score - left.score;
    return left.start.getTime() - right.start.getTime();
  });
  return {
    start: candidateSlots[0].start,
    end: candidateSlots[0].end,
  };
}

function taskPriorityColor(priority: number): string {
  if (priority >= 3) return "#C8795D";
  if (priority === 2) return "#7A9467";
  return "#6C95B3";
}

function tokenizeGoalLinkValues(...values: Array<string | null | undefined>): string[] {
  const tokens = new Set<string>();
  for (const value of values) {
    if (!value) continue;
    for (const token of value.toLowerCase().split(/[^a-z0-9]+/)) {
      const normalized = token.trim();
      if (normalized.length < 3 || GOAL_LINK_STOP_WORDS.has(normalized)) continue;
      tokens.add(normalized);
    }
  }
  return Array.from(tokens);
}

function hasStrongGoalAnchor(
  goal: Pick<HomeRelatedGoal, "title" | "category"> & { description?: string | null },
  candidateValues: Array<string | null | undefined>
): boolean {
  const goalTokens = tokenizeGoalLinkValues(goal.title, goal.description, goal.category ?? undefined);
  const candidateTokens = tokenizeGoalLinkValues(...candidateValues);
  if (goalTokens.length === 0 || candidateTokens.length === 0) return false;

  const sharedTokens = candidateTokens.filter((token) => goalTokens.includes(token));
  if (sharedTokens.length === 0) return false;
  if (sharedTokens.some((token) => token.length >= 6)) return true;
  return sharedTokens.length >= 2;
}

function buildGoalLinkAwareMessage(message: string, goalTitle?: string | null): string {
  const normalizedGoalTitle = goalTitle ? sanitizeSurfaceTitle(goalTitle) : null;
  if (!normalizedGoalTitle) return message;
  if (message.includes(`Linked to ${normalizedGoalTitle}.`)) return message;
  return `${message} Linked to ${normalizedGoalTitle}.`;
}

function deriveCaptureRelatedGoal(
  params: {
    sourceText: string;
    previewTitle: string | null;
    extractedAction: string | null;
    interpretation: HomeInterpretation | null;
    taskBreakdown: TaskBreakdown | null;
    goalDraft: GoalDraft | null;
    activeGoals: GoalItem[];
    goalMatch: {
      goal_id: string | null;
      goal_title: string | null;
      category: string | null;
      confidence: number;
    } | null;
  }
): HomeRelatedGoal | null {
  const {
    sourceText,
    previewTitle,
    extractedAction,
    interpretation,
    taskBreakdown,
    goalDraft,
    activeGoals,
    goalMatch,
  } = params;

  const candidateValues = [
    previewTitle,
    extractedAction,
    taskBreakdown?.title,
    taskBreakdown?.first_action,
    goalDraft?.title,
    sourceText,
  ];
  const isGoalUpdateCapture = GOAL_UPDATE_CAPTURE_PATTERN.test(sourceText);

  if (goalMatch?.goal_id && goalMatch.confidence >= 0.7) {
    const storedGoal = activeGoals.find((goal) => goal.id === goalMatch.goal_id);
    const goalTitle = goalMatch.goal_title || storedGoal?.title || "";
    const hasGoalAnchor = hasStrongGoalAnchor(
      {
        title: goalTitle,
        category: goalMatch.category ?? storedGoal?.category ?? null,
        description: storedGoal?.description ?? null,
      },
      candidateValues
    );
    if (!(isGoalUpdateCapture && !hasGoalAnchor)) {
      const shouldAutoAttach = isGoalUpdateCapture && hasGoalAnchor;
      const confidence = shouldAutoAttach
        ? Math.max(goalMatch.confidence, 0.92)
        : goalMatch.confidence;
      return {
        id: goalMatch.goal_id,
        title: goalTitle,
        category: goalMatch.category || undefined,
        confidence,
        reason: `Matched to ${goalMatch.goal_title}.`,
        linkMode: shouldAutoAttach || confidence >= 0.9 ? "auto" : "suggested",
      };
    }
  }

  const interpretedGoal = interpretation?.related_goal;
  if (!interpretedGoal?.id || !isGoalUpdateCapture) {
    return null;
  }

  const storedGoal = activeGoals.find((goal) => goal.id === interpretedGoal.id);
  const goalTitle = interpretedGoal.title || storedGoal?.title || "";
  if (!goalTitle) return null;

  if (
    !hasStrongGoalAnchor(
      {
        title: goalTitle,
        category: interpretedGoal.category ?? storedGoal?.category ?? null,
        description: storedGoal?.description ?? null,
      },
      candidateValues
    )
  ) {
    return null;
  }

  const confidence = Math.max(interpretedGoal.confidence ?? 0, 0.92);
  return {
    id: interpretedGoal.id,
    title: goalTitle,
    category: interpretedGoal.category ?? storedGoal?.category ?? undefined,
    confidence,
    reason:
      interpretedGoal.reason ||
      `Matched to ${goalTitle} because this reads like an update to an existing goal step.`,
    linkMode: confidence >= 0.9 ? "auto" : "suggested",
  };
}

function formatInterpretationLabel(primaryType: HomeInterpretation["primary_type"] | undefined): string {
  if (!primaryType) return "AI read";
  return primaryType.replaceAll("_", " ");
}

function formatCategoryLabel(category: string | null | undefined): string {
  return (category || "item").replaceAll("_", " ");
}

function withIndefiniteArticle(label: string): string {
  return /^[aeiou]/i.test(label) ? `an ${label}` : `a ${label}`;
}

function buildCorrectedCaptureMessage(category: string, action?: string | null): string {
  const label = formatCategoryLabel(category);
  if (action) {
    return `Updated: I’ll treat this as ${withIndefiniteArticle(label)}.\nNext workable move: ${action}`;
  }
  return `Updated: I’ll treat this as ${withIndefiniteArticle(label)} from here.`;
}

function buildRouteMessage(
  recommendedRouteKind: string | null | undefined,
  proposals: CaptureRouteProposal[],
  fallback?: string | null
): string {
  const recommended = proposals.find((proposal) => proposal.kind === recommendedRouteKind) || proposals[0];
  const headline = fallback?.trim();
  if (!recommended) return headline || "";
  return [headline, `Suggested route: ${recommended.label}.`].filter(Boolean).join("\n");
}

function describeRouteProposal(
  proposal: CaptureRouteProposal,
  recommendedRouteKind: string | null | undefined
): {
  stateLabel: string;
  supportLabel: string;
  accentClassName: string;
} {
  if (proposal.kind === recommendedRouteKind) {
    return {
      stateLabel: "Suggested",
      supportLabel: proposal.requires_confirmation ? "Needs confirmation before writing" : "Ready to commit here",
      accentClassName: "bg-forest-med text-white",
    };
  }

  if (proposal.kind === "capture_only") {
    return {
      stateLabel: "Keep for now",
      supportLabel: "Stays at the capture layer until you route it again",
      accentClassName: "bg-sand text-text",
    };
  }

  if (proposal.requires_confirmation || proposal.authority_level === "explicit_confirmation") {
    return {
      stateLabel: "Needs confirm",
      supportLabel: "Higher-impact write that should stay explicit",
      accentClassName: "bg-card text-text-light",
    };
  }

  if (proposal.authority_level === "auto_allowed") {
    return {
      stateLabel: "Low friction",
      supportLabel: "Can commit in place without extra setup",
      accentClassName: "bg-blue/10 text-blue",
    };
  }

  return {
    stateLabel: "Alternative",
    supportLabel: "Available route, but not the recommended default",
    accentClassName: "bg-card text-text-light",
  };
}

function findRouteProposal(
  proposals: CaptureRouteProposal[],
  kind: CaptureRouteProposal["kind"]
): CaptureRouteProposal | null {
  return proposals.find((proposal) => proposal.kind === kind) ?? null;
}

function buildCommitConfirmation(
  proposal: CaptureRouteProposal
): CaptureCommitConfirmation {
  return {
    decision: "confirm",
    proposal_id: proposal.id,
    target_kind: proposal.kind,
    authority_level: proposal.authority_level,
    destination_layer: proposal.destination_layer,
    owning_service: "capture_routing",
  };
}

function buildCaptureCommitConfirmation(
  commitment: CaptureCommitState["commitment"],
  // U14: pass the current restart task id so the confirmation can
  // reveal the Restart card directly when the new task IS the
  // restart, instead of pointing at a queue item Today filters out.
  activeRestartTaskId: string | null = null
): {
  state: CrossSurfaceConfirmationStateKind;
  message: string;
  ctaLabel?: string | null;
  ctaTab?: string | null;
  revealTarget?: CrossSurfaceRevealTarget | null;
  truth?: CrossSurfaceConfirmationTruth | null;
} {
  const resolvedConfirmation = commitment.resolved_confirmation;
  const truth: CrossSurfaceConfirmationTruth = {
    targetKind: commitment.target_kind,
    targetId: commitment.target_id,
    destinationLayer: commitment.destination_layer,
    owningService: resolvedConfirmation?.owning_service ?? "capture_routing",
    proposalId: commitment.resolved_proposal_id,
    authorityLevel: resolvedConfirmation?.authority_level ?? commitment.authority_level,
    requiresConfirmation: commitment.requires_confirmation,
  };

  if (commitment.commitment_state === "retained_capture") {
    return {
      state: "retained",
      message: commitment.landing_message,
      truth,
    };
  }

  let ctaLabel: string | null = null;
  let ctaTab: string | null = null;
  let revealTarget: CrossSurfaceRevealTarget | null = null;

  if (commitment.target_kind === "task" && commitment.destination_layer === "today") {
    ctaLabel = "Reveal item";
    ctaTab = "present";
    if (activeRestartTaskId && activeRestartTaskId === commitment.target_id) {
      // The captured task became the new Restart. Reveal the Restart
      // card, not a queue item that Today filters out.
      revealTarget = {
        surface: "present",
        kind: "today_restart",
        itemId: commitment.target_id,
      };
    } else {
      revealTarget = {
        surface: "present",
        kind: "today_queue_item",
        itemId: commitment.target_id,
      };
    }
  } else if (commitment.target_kind === "goal") {
    ctaLabel = "Open goal";
    ctaTab = "goals";
    revealTarget = {
      surface: "goals",
      kind: "goal_focus",
      goalId: commitment.target_id,
    };
  } else if (commitment.target_kind === "decision") {
    ctaLabel = "Open decision";
    ctaTab = "decide";
  }

  return {
    state: "committed",
    message: commitment.landing_message,
    ctaLabel,
    ctaTab,
    revealTarget,
    truth,
  };
}

function compactDraftSummary(summary?: string | null, description?: string | null, maxChars: number = 110): string {
  const source = (summary || description || "").replace(/\s+/g, " ").trim();
  if (!source) return "Turn this into a clearer goal with a realistic first step.";
  if (source.length <= maxChars) return source;
  const clipped = source.slice(0, maxChars).split(" ");
  clipped.pop();
  return `${(clipped.join(" ").trim() || source.slice(0, maxChars).trim()).replace(/[ ,.;:]+$/, "")}…`;
}

function moodEmoji(mood: string): string {
  switch (mood) {
    case "positive":
      return "😊";
    case "excited":
      return "🎉";
    case "negative":
      return "😔";
    case "anxious":
      return "😟";
    case "reflective":
      return "🌿";
    case "neutral":
      return "·";
    default:
      return "·";
  }
}

function scoreLabel(value: number, highLabel: string, mediumLabel: string): string {
  if (value >= 0.72) return highLabel;
  if (value >= 0.4) return mediumLabel;
  return "low";
}

function isQueuePlacement(location: TodayPlacement["location"]): boolean {
  return location === "queue" || location === "queue_peek";
}

function extractCapturedUrl(text: string): string | null {
  const match = text.match(URL_CAPTURE_PATTERN);
  if (!match) return null;
  return match[0].replace(/[),.;!?]+$/, "");
}

function extractRequestedTimingLabel(text: string): string | null {
  const normalized = text.trim().toLowerCase();
  if (!normalized) return null;

  const phrases = [
    "tomorrow morning",
    "tomorrow afternoon",
    "tomorrow evening",
    "tomorrow night",
    "this morning",
    "this afternoon",
    "this evening",
    "tonight",
    "tomorrow",
    "today",
  ];

  for (const phrase of phrases) {
    if (normalized.includes(phrase)) {
      return phrase;
    }
  }

  return null;
}

function buildTaskCaptureConfirmation(
  taskId: string,
  message: string,
  options?: { requestedTimingLabel?: string | null }
): {
  state: CrossSurfaceConfirmationStateKind;
  message: string;
  ctaLabel?: string | null;
  ctaTab?: string | null;
  revealTarget?: CrossSurfaceRevealTarget | null;
  truth: CrossSurfaceConfirmationTruth;
} {
  const requestedTimingLabel = options?.requestedTimingLabel?.trim() || null;
  return {
    state: "committed",
    message,
    ctaLabel: requestedTimingLabel ? "Open plan" : "Reveal item",
    ctaTab: requestedTimingLabel ? "schedule-plan" : "present",
    revealTarget: requestedTimingLabel
      ? null
      : {
          surface: "present",
          kind: "today_queue_item",
          itemId: taskId,
        },
    truth: {
      targetKind: "task",
      targetId: taskId,
      destinationLayer: "today",
      owningService: "capture_routing",
      authorityLevel: "light_confirmation",
      requiresConfirmation: true,
    },
  };
}

function buildScheduledTaskCaptureConfirmation(
  blockId: string,
  message: string,
  planDate: string
): {
  state: CrossSurfaceConfirmationStateKind;
  message: string;
  ctaLabel: string;
  ctaTab: string;
  revealTarget: CrossSurfaceRevealTarget;
  truth: CrossSurfaceConfirmationTruth;
} {
  return {
    state: "committed",
    message,
    ctaLabel: "Reveal item",
    // "schedule" (not "schedule-plan") — the Reveal CTA is for
    // *looking at* the just-placed block, not for kicking off a new
    // AI plan. Routing through "schedule-plan" bumps
    // scheduleAutoOpenPlanToken in app/page.tsx and auto-fires
    // handleAIPlan on top of the user, which is wrong intent.
    ctaTab: "schedule",
    revealTarget: {
      surface: "schedule-plan",
      kind: "schedule_block",
      blockId,
      planDate,
    },
    truth: {
      targetKind: "schedule_block",
      targetId: blockId,
      destinationLayer: "schedule-plan",
      owningService: "schedule",
    },
  };
}

function buildUnscheduledTaskTimingMessage(title: string, requestedTimingLabel: string): string {
  return `Created ${title} in Today, but left it unscheduled. ${requestedTimingLabel.charAt(0).toUpperCase()}${requestedTimingLabel.slice(1)} is not on the Plan yet.`;
}

function isDecisionSignal(
  text: string,
  interpretation: HomeInterpretation | null,
  recommendedRouteKind: string | null
): boolean {
  const normalized = text.trim();
  if (!normalized) return false;
  if (interpretation?.primary_type === "decision_help") return true;
  if (recommendedRouteKind === "decision") return true;
  return normalized.endsWith("?") || DECISION_SIGNAL_PATTERN.test(normalized);
}

function inferLibraryTitle(
  text: string,
  preview: QueueContentPreview | null,
  interpretation: HomeInterpretation | null,
  routeState: {
    clarified: {
      compression_summary?: string | null;
      top_themes?: string[];
    };
  }
): string {
  if (preview?.title?.trim()) return preview.title.trim();
  const goalTitle = interpretation?.goal_draft_preview?.title?.trim();
  if (goalTitle) return goalTitle;
  const theme = routeState.clarified.top_themes?.find((item) => item.trim().length > 0);
  if (theme) return theme.trim();
  const summary = routeState.clarified.compression_summary?.trim();
  if (summary) return summary.split(/(?<=[.!?])\s+/)[0]?.trim() || summary;
  const normalized = text.trim().replace(/^https?:\/\//i, "");
  return normalized.length > 96 ? `${normalized.slice(0, 93).trim()}...` : normalized;
}

function buildLibraryConfirmationMessage(
  item: {
    title: string;
    content_type: string | null;
    goal_title?: string | null;
    goal_match_pct?: number | null;
  },
  summary: SummarizeResponse | null
): string {
  const typeLabel = item.content_type ? item.content_type.replace(/_/g, " ") : "reference";
  const goalLabel = item.goal_title
    ? ` Goal fit: ${sanitizeSurfaceTitle(item.goal_title)}${
        item.goal_match_pct != null ? ` (${item.goal_match_pct}% match)` : ""
      }.`
    : "";
  const conciseSummary =
    summary?.summary.goal_relevance?.trim() ||
    summary?.summary.key_points?.[0]?.trim() ||
    "";
  return [
    `Saved ${sanitizeSurfaceTitle(item.title)} to Library as ${typeLabel}.`,
    goalLabel.trim(),
    conciseSummary,
  ]
    .filter(Boolean)
    .join(" ");
}

function buildDecisionConfirmationMessage(question: string, context: string | null): string {
  const cleanQuestion = sanitizeSurfaceTitle(question.trim() || "this question");
  const supportingContext = context?.trim();
  return supportingContext
    ? `Opened ${cleanQuestion} in Decisions. ${supportingContext}`
    : `Opened ${cleanQuestion} in Decisions so it stays a choice, not hidden work.`;
}

function timeStringToMinutes(value: string | null | undefined, fallbackMinutes: number): number {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) return fallbackMinutes;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

function deriveDayPhase(now: Date, quietHoursStart: string | null | undefined): UserDayPhase {
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const windDownMinutes = timeStringToMinutes(quietHoursStart, 22 * 60);
  const eveningStartMinutes = Math.max(17 * 60, windDownMinutes - 5 * 60);
  return nowMinutes >= eveningStartMinutes ? "evening" : "morning";
}

function buildStartHereFocus(
  blocks: ScheduleBlock[],
  restart: StartHereFocus | null,
  now: Date
): StartHereFocus | null {
  // Identity rule: the backend's `restart` payload is the canonical
  // answer to "which task should the user restart on?". Schedule
  // blocks only contribute *timing* and *duration enrichment* — they
  // never override identity. Without this rule, an orphan ScheduleBlock
  // (e.g. a drag-test residue with task_id=null titled "New block")
  // could swap the restart card's title/duration/time out from under
  // the backend's pick, producing a card stitched from three different
  // database rows.
  const eligibleBlocks = [...blocks]
    .filter((block) => !block.is_completed && SCHEDULE_FOCUS_KINDS.has(block.kind))
    .sort((a, b) => parseUTCishDate(a.start_time).getTime() - parseUTCishDate(b.start_time).getTime());

  const nowMs = now.getTime();
  const blockMinutes = (block: ScheduleBlock): number =>
    Math.max(
      15,
      Math.round(
        (parseUTCishDate(block.end_time).getTime() -
          parseUTCishDate(block.start_time).getTime()) /
          60000
      )
    );
  const blockTimingLabel = (block: ScheduleBlock, isCurrent: boolean): string =>
    isCurrent
      ? `${formatTime(block.start_time)} - ${formatTime(block.end_time)}`
      : `Starts ${formatTime(block.start_time)}`;

  // Active-block override: if RIGHT NOW falls inside a scheduled
  // block, that block's task is the user's actual focus regardless
  // of what the backend's restart picker said. The schedule wins
  // over heuristics — if the user committed to a 5:00–5:30 PM
  // session and it's 5:12, that's their focus, period. This is the
  // industrial-standard interpretation of a calendar/timebox app.
  const activeNowBlock =
    eligibleBlocks.find((block) => {
      const start = parseUTCishDate(block.start_time).getTime();
      const end = parseUTCishDate(block.end_time).getTime();
      return start <= nowMs && end > nowMs;
    }) ?? null;
  if (activeNowBlock) {
    const endMs = parseUTCishDate(activeNowBlock.end_time).getTime();
    const remainingMinutes = Math.max(0, (endMs - nowMs) / 60000);
    return {
      id: activeNowBlock.task_id ?? activeNowBlock.id,
      title: activeNowBlock.title,
      why_now:
        activeNowBlock.description?.trim() ||
        activeNowBlock.rationale?.trim() ||
        "This is the block your schedule says to be in right now.",
      confidence: "high",
      effort_minutes: blockMinutes(activeNowBlock),
      goal:
        activeNowBlock.goal_id && activeNowBlock.goal_title
          ? {
              id: activeNowBlock.goal_id,
              title: activeNowBlock.goal_title,
            }
          : null,
      source_label: "In progress now",
      timing_label: blockTimingLabel(activeNowBlock, true),
      // Auto-start the timer with the time the user actually has
      // left in this block, and auto-complete the task when the
      // block's end_time hits — no extra Begin / Done taps needed.
      auto_start_minutes: remainingMinutes,
      auto_complete_at_end: true,
    };
  }

  if (restart && restart.id) {
    // Try to find a block that represents the same identity as the
    // backend's restart pick. Only that block may contribute timing.
    const matchingBlock =
      eligibleBlocks.find((block) => block.task_id === restart.id) ?? null;
    if (matchingBlock) {
      const start = parseUTCishDate(matchingBlock.start_time).getTime();
      const end = parseUTCishDate(matchingBlock.end_time).getTime();
      const isCurrent = start <= nowMs && end > nowMs;
      return {
        ...restart,
        // Sync duration with the block — single source of truth.
        effort_minutes: blockMinutes(matchingBlock),
        timing_label: blockTimingLabel(matchingBlock, isCurrent),
        source_label: isCurrent ? "Scheduled now" : "Up next",
        confidence: isCurrent ? "high" : restart.confidence,
      };
    }
    // Backend has a restart but no matching block — keep restart
    // as-is. Do NOT promote a different block's identity into this
    // card. timing_label stays null so RestartBlock won't render
    // a fabricated time.
    return restart;
  }

  // No backend restart — legacy behavior: use the current/next block
  // as the focus when nothing better is available.
  const currentBlock =
    eligibleBlocks.find((block) => {
      const start = parseUTCishDate(block.start_time).getTime();
      const end = parseUTCishDate(block.end_time).getTime();
      return start <= nowMs && end > nowMs;
    }) ?? null;

  if (currentBlock) {
    return {
      id: currentBlock.task_id ?? currentBlock.id,
      title: currentBlock.title,
      why_now:
        currentBlock.description?.trim() ||
        currentBlock.rationale?.trim() ||
        "This is the block your schedule says to be in right now.",
      confidence: "high",
      effort_minutes: blockMinutes(currentBlock),
      goal:
        currentBlock.goal_id && currentBlock.goal_title
          ? {
              id: currentBlock.goal_id,
              title: currentBlock.goal_title,
            }
          : null,
      source_label: "Scheduled now",
      timing_label: blockTimingLabel(currentBlock, true),
    };
  }

  const nextBlock =
    eligibleBlocks.find((block) => parseUTCishDate(block.end_time).getTime() > nowMs) ?? null;

  if (nextBlock) {
    return {
      id: nextBlock.task_id ?? nextBlock.id,
      title: nextBlock.title,
      why_now:
        nextBlock.description?.trim() ||
        nextBlock.rationale?.trim() ||
        "This is the next scheduled block waiting for you today.",
      confidence: "medium",
      effort_minutes: blockMinutes(nextBlock),
      goal:
        nextBlock.goal_id && nextBlock.goal_title
          ? {
              id: nextBlock.goal_id,
              title: nextBlock.goal_title,
            }
          : null,
      source_label: "Up next",
      timing_label: blockTimingLabel(nextBlock, false),
    };
  }

  return null;
}

export default function TodayScreen({
  onNavigate,
  isFirstSessionPending = false,
  onFirstSessionComplete,
}: Props) {
  const { user } = useAuth();
  const { projection, status, error, refresh, setProjection } = useTodayProjection();
  const lastTaskChange = useDataRefresh((state) => state.lastTaskChange);
  const lastGoalChange = useDataRefresh((state) => state.lastGoalChange);
  const lastScheduleChange = useDataRefresh((state) => state.lastScheduleChange);
  const notifyTaskChange = useDataRefresh((state) => state.notifyTaskChange);
  const notifyScheduleChange = useDataRefresh((state) => state.notifyScheduleChange);
  // Completing a task cascades to goal progress (backend ADR-0005); fire the
  // goal notifier too so a mounted Goals screen reflects it without a remount.
  const notifyGoalChange = useDataRefresh((state) => state.notifyGoalChange);
  const setCarryForwardSummary = useCarryForwardContext((state) => state.setSummary);
  const crossSurfaceConfirmation = useCrossSurfaceConfirmationStore((state) => state.confirmation);
  const showCrossSurfaceConfirmation = useCrossSurfaceConfirmationStore((state) => state.showConfirmation);
  const triggerCrossSurfaceReveal = useCrossSurfaceConfirmationStore((state) => state.triggerReveal);
  const [captureResult, setCaptureResult] = useState<CaptureResultState | null>(null);
  // Intent Preview accept/dismiss state (U3.3). Managed here so that
  // `handleCreateTaskFromCapture` can honor the user's choice when
  // committing. Defaults: `"auto"` linkMode → accepted. `"suggested"`
  // linkMode → pending (user must opt in). No goal → not applicable.
  const [captureGoalDecision, setCaptureGoalDecision] = useState<
    "accepted" | "dismissed" | "pending"
  >("pending");
  useEffect(() => {
    const link = captureResult?.relatedGoal?.linkMode;
    if (link === "auto") {
      setCaptureGoalDecision("accepted");
    } else if (link === "suggested") {
      setCaptureGoalDecision("pending");
    } else {
      setCaptureGoalDecision("pending");
    }
  }, [captureResult?.relatedGoal?.id, captureResult?.relatedGoal?.linkMode]);
  const [feedbackBanner, setFeedbackBanner] = useState<FeedbackBannerState | null>(null);
  const [captureActionLoading, setCaptureActionLoading] = useState<string | null>(null);
  const [correctionMode, setCorrectionMode] = useState(false);
  // Evening wrap popup — auto-opens once after 5 PM (mirrors morning brief
  // gate). Replaces the legacy EndOfDayReviewSheet + inline "Evening review"
  // cards. Per-task triage (Done / Tomorrow / Smaller / Release) lives
  // inside the popup itself.
  const [showEveningPopup, setShowEveningPopup] = useState(false);
  const [eveningBriefCache, setEveningBriefCache] = useState<EveningBrief | null>(null);
  const [showBrief, setShowBrief] = useState(false);
  const [focusSignal, setFocusSignal] = useState(0);
  const [startedRestartId, setStartedRestartId] = useState<string | null>(null);
  const [restartActionLoading, setRestartActionLoading] = useState<"later" | "smaller" | null>(null);
  const [capturePendingText, setCapturePendingText] = useState<string | null>(null);
  const [highlightedQueueItemId, setHighlightedQueueItemId] = useState<string | null>(null);
  const [fullQueueItems, setFullQueueItems] = useState<TaskItem[]>([]);
  const [fullQueueExpanded, setFullQueueExpanded] = useState(false);
  const [fullQueueLoading, setFullQueueLoading] = useState(false);
  const [activeGoals, setActiveGoals] = useState<GoalItem[]>([]);
  const [lifeAllocation, setLifeAllocation] = useState<LifeAllocation | null>(null);
  const [scheduleBlocks, setScheduleBlocks] = useState<ScheduleBlock[]>([]);
  const [quietHoursStart, setQuietHoursStart] = useState<string | null>(null);
  const { isChatOpen, isSettingsOpen, isNewDecisionOpen } = useOverlayManager();
  const blockingOverlayOpen = isChatOpen || isSettingsOpen || isNewDecisionOpen;
  const queueSectionRef = useRef<HTMLDivElement | null>(null);
  const queueHighlightTimeoutRef = useRef<number | null>(null);
  const captureRequestIdRef = useRef(0);

  const cleanMessage = captureResult
    ? cleanCaptureMessage(captureResult.agentMessage, captureResult.action)
    : "";
  const primaryInterpretation = captureResult?.interpretation?.primary_type;
  const activeGoalMap = useMemo(
    () =>
      new Map(
        activeGoals.map((goal) => [
          goal.id,
          {
            title: goal.title,
            category: goal.category ?? null,
          },
        ])
      ),
    [activeGoals]
  );
  const restartFocus = useMemo<StartHereFocus | null>(
    () =>
      projection?.restart
        ? {
            ...projection.restart,
            source_label: "Next open task",
            timing_label: null,
          }
        : null,
    [projection?.restart]
  );
  // Ticks every 30s so the "active block" / "remaining minutes"
  // computation in buildStartHereFocus refreshes without a full
  // network reload. Without this the focus card doesn't switch when
  // the scheduled start/end time crosses, and the auto-start timer
  // would carry stale remaining-minutes after the user idles.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);
  const autoFocus = useMemo(
    () => buildStartHereFocus(scheduleBlocks, restartFocus, new Date(nowTick)),
    [restartFocus, scheduleBlocks, nowTick]
  );
  const [promotedFocus, setPromotedFocus] = useState<StartHereFocus | null>(null);
  const startHereFocus = promotedFocus ?? autoFocus;
  useChatRevealHighlight("today", startHereFocus?.id ?? projection?.restart?.id ?? "");
  const dayPhase = useMemo(() => deriveDayPhase(new Date(), quietHoursStart), [quietHoursStart]);

  const handlePromoteToFocus = useCallback((task: TaskItem) => {
    const goalInfo = task.goal_id ? activeGoalMap.get(task.goal_id) : null;
    setPromotedFocus({
      id: task.id,
      title: task.title,
      why_now: goalInfo ? `Linked to ${goalInfo.title}.` : "You chose to focus on this.",
      confidence: "high",
      effort_minutes: task.estimated_minutes ?? null,
      goal: goalInfo ? { id: task.goal_id!, title: goalInfo.title } : null,
      source_label: "Your pick",
    });
  }, [activeGoalMap]);

  const completeFirstSession = useCallback(() => {
    if (!isFirstSessionPending) return;
    onFirstSessionComplete?.();
  }, [isFirstSessionPending, onFirstSessionComplete]);

  useEffect(() => {
    if (showBrief && blockingOverlayOpen) {
      setShowBrief(false);
    }
  }, [blockingOverlayOpen, showBrief]);

  useEffect(() => {
    if (isFirstSessionPending) return;
    if (status !== "ready") return;
    if (blockingOverlayOpen) return;
    const today = new Date().toDateString();
    const lastShown = localStorage.getItem("de_brief_shown");
    const hour = new Date().getHours();
    if (lastShown === today || hour >= 12) return;

    // Ambient daily briefs should never steal focus from active work.
    // They wait until Today is ready and no modal/sheet is competing.
    const id = window.setTimeout(() => {
      localStorage.setItem("de_brief_shown", today);
      setShowBrief(true);
    }, MORNING_BRIEF_IDLE_DELAY_MS);

    return () => window.clearTimeout(id);
  }, [blockingOverlayOpen, isFirstSessionPending, status]);

  // Evening wrap auto-fire — once per evening, on first sign-in after the
  // user's evening threshold (deriveDayPhase puts that around 5 PM, sliding
  // earlier if quiet hours start earlier). Also prefetches the brief so
  // the popup mounts with data ready.
  useEffect(() => {
    if (isFirstSessionPending) return;
    if (status !== "ready") return;
    if (showBrief) return;
    if (dayPhase !== "evening") return;
    // Quiet-day gate: don't fire the wrap on a day with zero
    // completions. The popup is about reflecting on the day; firing
    // it on an empty day (e.g. a fresh account that just landed
    // post-onboarding) hijacks the user's first-use moment with
    // "rate the day" / "release tasks" controls that mean nothing
    // yet. The follow-up audit explicitly flagged this as the
    // worst remaining first-use trust hit.
    if ((projection?.completed_today_count ?? 0) === 0) return;
    const today = new Date().toDateString();
    const lastShown = localStorage.getItem("de_evening_shown");
    if (lastShown === today) return;
    // Prefetch in parallel: kick the brief request first so it's likely
    // resolved by the time the popup mounts and its useEffect runs.
    // Evening phase reorders top_tasks so today's slipped items lead.
    void getMorningBrief({ phase: "evening" })
      .then((data) => setEveningBriefCache(data))
      .catch(() => setEveningBriefCache(null));
    setShowEveningPopup(true);
    localStorage.setItem("de_evening_shown", today);
  }, [
    dayPhase,
    isFirstSessionPending,
    projection?.completed_today_count,
    showBrief,
    status,
  ]);

  useEffect(() => {
    if (status !== "ready") return;
    const today = formatLocalDateInput(new Date());
    void (async () => {
      try {
        const [goalsResponse, planningOverview, scheduleResponse, settings] = await Promise.all([
          listGoals("active").catch(() => ({ goals: [], total: 0 })),
          getPlanningOverview(today).catch(() => null),
          getSchedule(today).catch(() => ({ blocks: [], total: 0 })),
          getSettings().catch(() => null),
        ]);

        setActiveGoals(goalsResponse.goals);
        setLifeAllocation(planningOverview?.life_allocation ?? null);
        setScheduleBlocks(scheduleResponse.blocks ?? []);
        setQuietHoursStart(settings?.quiet_hours_start ?? null);
      } catch {
        // Keep Today usable even if supporting context fails.
      }
    })();
  }, [lastGoalChange, lastScheduleChange, status]);

  useEffect(() => {
    if (!isFirstSessionPending) return;
    if (status !== "ready") return;
    if (!startHereFocus && fullQueueItems.length === 0) return;
    completeFirstSession();
  }, [completeFirstSession, fullQueueItems.length, isFirstSessionPending, startHereFocus, status]);

  const showTransientToast = useCallback((
    message: string,
    timeout: number = 3200,
    options?: { ctaLabel?: string; onCta?: (() => void) | null }
  ) => {
    const token = Date.now();
    setFeedbackBanner({
      token,
      message,
      ctaLabel: options?.ctaLabel,
      onCta: options?.onCta ?? null,
    });
    setTimeout(() => {
      setFeedbackBanner((current) => (current?.token === token ? null : current));
    }, timeout);
  }, []);

  const handleCompleteFocus = useCallback(async (focusId: string) => {
    // Delegate the mutation sequence to a pure helper that does NOT swallow
    // failures (bug B2/B3/B6). The success path below only runs on { ok }.
    const result = await completeFocusItem(focusId, scheduleBlocks, {
      completeScheduleBlock,
      updateTask,
    });

    if (result.status === "ok") {
      // Fire every dependent notifier (a completion can move task lists, the
      // timeline, and goal progress) — only after the write actually landed.
      notifyTaskChange();
      notifyScheduleChange();
      notifyGoalChange();
      setPromotedFocus(null);
      // No "Done!" toast — the in-card celebration animation is the
      // confirmation. Stacking a top-of-screen banner on top of it felt
      // redundant.
      refresh();
      return;
    }

    // A required write failed — surface it instead of a silent split state.
    {
      const err = result.error;
      const detail = err instanceof Error ? err.message : String(err);
      // Backend auth.py returns either "Not authenticated" (line 96) or
      // "Invalid or expired token" (line 74). The api client also surfaces
      // "(HTTP 401)" via readJson. Catch all three.
      const looksLikeAuth = /not authenticated|unauthorized|401|expired token|invalid token/i.test(detail);
      if (looksLikeAuth) {
        showTransientToast("Session expired — please sign in again.", 6000, {
          ctaLabel: "Sign in",
          onCta: () => {
            if (typeof window !== "undefined") {
              window.localStorage.removeItem("de_access_token");
              window.location.assign("/sign-in");
            }
          },
        });
      } else {
        showTransientToast(`Could not complete task: ${detail}`, 4000);
      }
    }
  }, [notifyScheduleChange, notifyTaskChange, notifyGoalChange, refresh, scheduleBlocks, showTransientToast]);

  const highlightQueueItem = useCallback((itemId: string | null, timeout: number = 5200) => {
    if (queueHighlightTimeoutRef.current) {
      window.clearTimeout(queueHighlightTimeoutRef.current);
      queueHighlightTimeoutRef.current = null;
    }

    setHighlightedQueueItemId(itemId);

    if (!itemId || timeout <= 0) {
      return;
    }

    queueHighlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedQueueItemId(null);
      queueHighlightTimeoutRef.current = null;
    }, timeout);
  }, []);

  const scrollQueueIntoView = useCallback(() => {
    window.requestAnimationFrame(() => {
      queueSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const loadFullQueue = useCallback(async () => {
    setFullQueueLoading(true);
    try {
      const response = await listTasks("pending", 100);
      // Skip the current restart, and skip any task whose only open
      // ScheduleBlock starts at or after the user's local end-of-
      // today. Otherwise tomorrow-only items leak into "TODAY'S
      // TASKS" while Plan correctly places them on the next day —
      // the cross-surface drift the follow-up audit flagged.
      const nowUtcMs = Date.now();
      const todayEndUtcMs = (() => {
        const now = new Date();
        const localEnd = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() + 1
        );
        return localEnd.getTime();
      })();
      // Index every linked block per task — earliest open block plus
      // whether ALL the task's open blocks are already in the past.
      // The latter is the "default-done" check: if the user scheduled
      // the work for 9:35 AM and it's now 6 PM, the block has aged
      // out and the task no longer belongs in Today's queue (it
      // either got done or got missed; either way it's not the
      // user's *next* move).
      const earliestOpenStartByTaskId = new Map<string, number>();
      const allOpenBlocksPastByTaskId = new Map<string, boolean>();
      for (const block of scheduleBlocks) {
        const taskId = block.task_id;
        if (!taskId || block.is_completed) continue;
        const startMs = parseUTCishDate(block.start_time).getTime();
        const endMs = parseUTCishDate(block.end_time).getTime();
        const prev = earliestOpenStartByTaskId.get(taskId);
        if (prev === undefined || startMs < prev) {
          earliestOpenStartByTaskId.set(taskId, startMs);
        }
        const wasAllPast = allOpenBlocksPastByTaskId.get(taskId);
        const thisIsPast = endMs <= nowUtcMs;
        if (wasAllPast === undefined) {
          allOpenBlocksPastByTaskId.set(taskId, thisIsPast);
        } else {
          allOpenBlocksPastByTaskId.set(taskId, wasAllPast && thisIsPast);
        }
      }
      const filteredTasks = response.tasks.filter((task) => {
        if (task.id === projection?.restart?.id) return false;
        if (task.id === startHereFocus?.id) return false;
        const earliest = earliestOpenStartByTaskId.get(task.id);
        if (earliest !== undefined && earliest >= todayEndUtcMs) return false;
        // Default-done rule: every open block tied to this task is
        // already in the past → the task has aged out of Today.
        const allPast = allOpenBlocksPastByTaskId.get(task.id);
        if (allPast === true) return false;
        return true;
      });
      setFullQueueItems(filteredTasks);
      return filteredTasks;
    } catch {
      showTransientToast("Could not load Today’s queue yet.", 4200);
      return null;
    } finally {
      setFullQueueLoading(false);
    }
  }, [
    projection?.restart?.id,
    scheduleBlocks,
    showTransientToast,
    startHereFocus?.id,
  ]);

  const openFullQueue = useCallback(async (options?: { highlightItemId?: string | null; scroll?: boolean }) => {
    setFullQueueExpanded(true);
    await loadFullQueue();
    highlightQueueItem(options?.highlightItemId ?? null);
    if (options?.scroll !== false) {
      scrollQueueIntoView();
    }
  }, [highlightQueueItem, loadFullQueue, scrollQueueIntoView]);

  const toggleFullQueue = useCallback(() => {
    if (fullQueueExpanded) {
      setFullQueueExpanded(false);
      highlightQueueItem(null, 0);
      return;
    }
    void openFullQueue();
  }, [fullQueueExpanded, highlightQueueItem, openFullQueue]);

  useEffect(() => {
    return () => {
      if (queueHighlightTimeoutRef.current) {
        window.clearTimeout(queueHighlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (status !== "ready") return;
    void loadFullQueue();
  }, [lastTaskChange, loadFullQueue, status]);

  useEffect(() => {
    if (status !== "ready") return;
    if (!projection) return;
    if (projection.restart) return;
    if ((projection.queue_peek?.length ?? 0) > 0) return;
    if (fullQueueItems.length !== 1) return;

    const fallbackProjection = applyFreshTodayRestartFallback(projection, [
      {
        id: fullQueueItems[0].id,
        title: fullQueueItems[0].title,
        status: fullQueueItems[0].status,
        estimated_minutes: fullQueueItems[0].estimated_minutes ?? undefined,
      },
    ]);

    if (!fallbackProjection.restart) return;
    setProjection(fallbackProjection);
  }, [fullQueueItems, projection, setProjection, status]);

  useEffect(() => {
    const revealTarget = crossSurfaceConfirmation?.revealTarget;
    if (!revealTarget || crossSurfaceConfirmation.revealNonce <= 0) return;
    if (revealTarget.surface !== "present") return;
    if (revealTarget.kind === "today_queue_item") {
      void openFullQueue({ highlightItemId: revealTarget.itemId });
      return;
    }
    if (revealTarget.kind === "today_restart") {
      // U14: scroll the Restart card into view + brief flash.
      // RestartBlock owns its own data-testid="restart-block" hook.
      const node =
        typeof document !== "undefined"
          ? document.querySelector<HTMLElement>('[data-testid="restart-block"]')
          : null;
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
        node.classList.add("ring-2", "ring-forest-med", "ring-offset-2", "transition");
        const t = setTimeout(() => {
          node.classList.remove("ring-2", "ring-forest-med", "ring-offset-2", "transition");
        }, 1800);
        return () => clearTimeout(t);
      }
    }
  }, [crossSurfaceConfirmation, openFullQueue]);

  const applyCaptureResponse = useCallback((res: CaptureResponse, routing?: Partial<CaptureRouting>) => {
    setCaptureResult({
      captureId: null,
      clarifiedId: null,
      category: res.thought.category,
      thoughtType: res.thought.thought_type ?? null,
      categoryConfidence: res.thought.category_confidence ?? 0.7,
      tags: res.thought.tags || [],
      mood: res.thought.mood,
      moodConfidence: res.thought.mood_confidence ?? 0,
      emotionalWeight: res.thought.emotional_weight ?? 0,
      ambiguityScore: res.thought.ambiguity_score ?? 0,
      compression: res.thought.compression ?? null,
      action: res.thought.extracted_action,
      cluster: res.thought.cluster ?? null,
      agentMessage: res.agent_message || "",
      sourceText: routing?.sourceText || "",
      interpretation: routing?.interpretation ?? null,
      goalDraft: routing?.goalDraft ?? null,
      taskBreakdown: routing?.taskBreakdown ?? null,
      relatedGoal: routing?.relatedGoal ?? null,
      routeProposals: [],
      recommendedRouteKind: null,
      isError: false,
      userCorrected: res.thought.user_corrected,
    });
    setCorrectionMode(false);
  }, []);

  const handleCorrection = useCallback(async (newCategory: "task" | "decision" | "reference" | "goal" | "schedule_block" | "capture_only") => {
    if (!captureResult?.captureId) return;
    const sourceText = captureResult.sourceText || captureResult.action || "";
    // Translate popup CaptureType -> backend heuristic category.
    // The backend's _normalize_route_kind has a different naming
    // convention than the popup; without these mappings, clicking
    // certain override pills routes the capture to the wrong kind
    // (e.g., "reference" without translation falls to
    // unclear_capture and the popup defaults to "Captured").
    //   - schedule_block -> schedule (U11)
    //   - capture_only   -> idea     (U13)
    //   - reference      -> link     (Bug #1 fix: backend has no
    //     "reference" classification category — only "link"
    //     normalizes to the reference route_kind.)
    const backendOverride =
      newCategory === "schedule_block"
        ? "schedule"
        : newCategory === "capture_only"
          ? "idea"
          : newCategory === "reference"
            ? "link"
            : newCategory;
    try {
      const updated = await classifyCapture(captureResult.captureId, backendOverride);
      const [routed, correctedGoalDraft, correctedTaskBreakdown] = await Promise.all([
        routeCapture(captureResult.captureId),
        newCategory === "goal" && sourceText
          ? getGoalDraft(sourceText).catch(() => null)
          : Promise.resolve(null),
        // schedule_block reuses the task title shape — fetch the
        // breakdown so the popup can still show first_action.
        (newCategory === "task" || newCategory === "schedule_block") && sourceText
          ? getTaskBreakdown(sourceText).catch(() => null)
          : Promise.resolve(null),
      ]);
      setCaptureResult((previous) =>
        previous
          ? {
              ...previous,
              category: updated.category,
              thoughtType: updated.thought_type ?? previous.thoughtType,
              categoryConfidence: updated.category_confidence ?? 1,
              action:
                correctedGoalDraft?.first_action ??
                correctedTaskBreakdown?.first_action ??
                updated.extracted_action ??
                previous.action,
              emotionalWeight: updated.emotional_weight ?? previous.emotionalWeight,
              ambiguityScore: updated.ambiguity_score ?? previous.ambiguityScore,
              compression: updated.compression_summary
                ? {
                    summary: updated.compression_summary,
                    top_themes: updated.top_themes ?? previous.compression?.top_themes ?? [],
                    what_needs_action: updated.what_needs_action ?? previous.compression?.what_needs_action ?? undefined,
                    what_can_wait: updated.what_can_wait ?? previous.compression?.what_can_wait ?? undefined,
                    emotional_signal: updated.emotional_signal ?? previous.compression?.emotional_signal ?? undefined,
                    focus_recommendation: updated.focus_recommendation ?? previous.compression?.focus_recommendation ?? undefined,
                  }
                : previous.compression,
              agentMessage: buildRouteMessage(
                routed.recommended_route_kind,
                routed.proposals,
                updated.focus_recommendation
                  ?? correctedGoalDraft?.analysis
                  ?? correctedTaskBreakdown?.summary
                  ?? updated.compression_summary
              ),
              interpretation: null,
              goalDraft: updated.category === "goal" ? correctedGoalDraft : null,
              taskBreakdown:
                updated.category === "task" || updated.category === "schedule"
                  ? correctedTaskBreakdown
                  : null,
              routeProposals: routed.proposals,
              recommendedRouteKind: routed.recommended_route_kind,
              userCorrected: true,
            }
          : null
      );
      setCorrectionMode(false);
      showTransientToast(`Updated — I’ll treat this as ${withIndefiniteArticle(formatCategoryLabel(updated.category))}.`);
    } catch {
      showTransientToast("Could not save that correction.", 4200);
    }
  }, [captureResult, showTransientToast]);

  const handleCapture = useCallback(async (
    text: string,
    opts?: { prefetchedRouteState?: CaptureRouteState },
  ) => {
    const requestId = Date.now();
    captureRequestIdRef.current = requestId;
    setCapturePendingText(text);
    setCaptureResult(null);
    setCorrectionMode(false);
    showTransientToast("Capturing...", 1500);
    try {
      // When the screenshot V2 endpoint already returned a fully-routed
      // capture state, skip the duplicate create+classify+route round
      // trip. Without this we'd build a second CaptureItem for the
      // same OCR'd text, defeating the point of the atomic endpoint.
      // Interpretation still runs in parallel because it's independent
      // of the capture-pipeline state.
      const capturePromise = opts?.prefetchedRouteState
        ? Promise.resolve(opts.prefetchedRouteState)
        : promiseWithTimeout((async () => {
            const capture = await createCapture(text, "text", "today");
            await classifyCapture(capture.id);
            return routeCapture(capture.id);
          })());
      const [captureOutcome, interpretationOutcome] = await Promise.allSettled([
        capturePromise,
        interpretHomeInput(text),
      ]);

      if (captureOutcome.status !== "fulfilled") {
        throw captureOutcome.reason;
      }

      let interpretation =
        interpretationOutcome.status === "fulfilled" ? interpretationOutcome.value : null;
      let goalDraft = interpretation?.goal_draft_preview ?? null;
      let taskBreakdown = interpretation?.task_breakdown_preview ?? null;

      if (interpretation?.primary_type === "goal_draft" && !goalDraft) {
        try {
          goalDraft = await getGoalDraft(text);
        } catch {
          goalDraft = null;
        }
      }

      if (interpretation?.primary_type === "task_breakdown" && !taskBreakdown) {
        try {
          taskBreakdown = await getTaskBreakdown(text);
        } catch {
          taskBreakdown = null;
        }
      }

      if (captureRequestIdRef.current !== requestId) {
        return;
      }

      const routeState = captureOutcome.value;
      const trimmedText = text.trim();

      // All captures go through the popup — no special auto-routing
      if (captureRequestIdRef.current !== requestId) return;

      // For URLs: fetch preview to get real title
      const sourceUrl = extractCapturedUrl(trimmedText);
      const preview = sourceUrl ? await previewContentCapture(sourceUrl).catch(() => null) : null;

      // Three-band Intent Preview rubric (U3.3 + U3.4):
      //   ≥ 0.90 → auto-attach with a visible "Not this one" escape
      //   0.70 – 0.90 → show suggestion chip; do not auto-attach
      //   < 0.70 → silent (no relatedGoal at all)
      //
      // U3.4 fix: route the goal-attachment decision through the strict
      // matcher (`/api/goals/match`) ONLY. The interpretation endpoint
      // carries its own older, looser `related_goal` that bypassed the
      // Subject-Lock prompt and produced the "Practiced piano chords" →
      // "Establish Evening Reading Habit" wrong-suggestion documented
      // in docs/2026-04-22-u3-verification-note.md (Scenario B).
      //
      // Consequence: `interpretation.related_goal` is intentionally
      // ignored for attachment. Its `category` hint is still indirectly
      // available through the classification step feeding the
      // life-area picker; no regression there.
      const matchText =
        preview?.title ||
        taskBreakdown?.title ||
        routeState.clarified.extracted_action ||
        goalDraft?.title ||
        trimmedText;
      const goalMatch = await matchTextToGoal(matchText).catch(() => null);
      const relatedGoal = deriveCaptureRelatedGoal({
        sourceText: trimmedText,
        previewTitle: preview?.title ?? null,
        extractedAction: routeState.clarified.extracted_action ?? null,
        interpretation,
        taskBreakdown,
        goalDraft,
        activeGoals,
        goalMatch,
      });

      setCapturePendingText(null);
      setCaptureResult({
        captureId: routeState.capture.id,
        clarifiedId: routeState.clarified.id,
        category: routeState.clarified.category,
        thoughtType: routeState.clarified.thought_type ?? null,
        categoryConfidence: routeState.clarified.category_confidence ?? 0.7,
        tags: routeState.clarified.tags || [],
        mood: routeState.clarified.mood ?? null,
        moodConfidence: routeState.clarified.mood_confidence ?? 0,
        emotionalWeight: routeState.clarified.emotional_weight ?? 0,
        ambiguityScore: routeState.clarified.ambiguity_score ?? 0,
        compression: routeState.clarified.compression_summary
          ? {
              summary: routeState.clarified.compression_summary,
              top_themes: routeState.clarified.top_themes || [],
              what_needs_action: routeState.clarified.what_needs_action ?? undefined,
              what_can_wait: routeState.clarified.what_can_wait ?? undefined,
              emotional_signal: routeState.clarified.emotional_signal ?? undefined,
              focus_recommendation: routeState.clarified.focus_recommendation ?? undefined,
            }
          : null,
        action: routeState.clarified.extracted_action ?? null,
        cluster: null,
        agentMessage: buildRouteMessage(
          routeState.recommended_route_kind,
          routeState.proposals,
          routeState.clarified.focus_recommendation || routeState.clarified.compression_summary
        ),
        sourceText: text,
        interpretation,
        goalDraft,
        taskBreakdown,
        relatedGoal,
        routeProposals: routeState.proposals,
        recommendedRouteKind: routeState.recommended_route_kind,
        isError: false,
        previewTitle: preview?.title ?? null,
      });
    } catch (captureError) {
      if (captureRequestIdRef.current !== requestId) {
        return;
      }

      setCapturePendingText(null);
      const message =
        captureError instanceof Error ? captureError.message : "Failed to capture. Try again.";
      setCaptureResult({
        captureId: null,
        clarifiedId: null,
        category: "error",
        thoughtType: null,
        categoryConfidence: 0,
        tags: [],
        mood: null,
        moodConfidence: 0,
        emotionalWeight: 0,
        ambiguityScore: 0,
        compression: null,
        action: null,
        cluster: null,
        agentMessage: message,
        sourceText: text,
        interpretation: null,
        goalDraft: null,
        taskBreakdown: null,
        relatedGoal: null,
        routeProposals: [],
        recommendedRouteKind: null,
        isError: true,
      });
    }
  }, [showTransientToast]);

  // ── Pending capture intent — Reflect "Try this" handoff (§4.4) ──
  // When SuggestionCard sets the intent, we auto-fire handleCapture(text)
  // on the next mount so the user lands on Today with CaptureResultPopup
  // already open and prefilled. Consume-on-read prevents re-fires.
  const pendingCaptureIntent = usePendingCaptureIntent((s) => s.intent);
  const consumePendingCaptureIntent = usePendingCaptureIntent(
    (s) => s.consumeIntent,
  );
  useEffect(() => {
    if (!pendingCaptureIntent) return;
    const intent = consumePendingCaptureIntent();
    if (!intent) return;
    void handleCapture(intent.prefillText);
    if (intent.sourceContext?.kind === "reflect_suggestion" && intent.sourceContext.id) {
      // Mark the source Recommendation as accepted so it stops resurfacing.
      // Best-effort: silently ignore failures (Today doesn't 500 on this).
      const recId = intent.sourceContext.id;
      void (async () => {
        try {
          const { authFetch } = await import("@/lib/api/base");
          await authFetch(`/api/recommendations/${recId}/mark-accepted`, {
            method: "POST",
          });
        } catch {
          // no-op
        }
      })();
    }
  }, [pendingCaptureIntent, consumePendingCaptureIntent, handleCapture]);

  const handleScreenshotCapture = useCallback((response: CaptureResponse) => {
    applyCaptureResponse(response, { sourceText: "" });
  }, [activeGoals, applyCaptureResponse]);

  const handleCreateGoalFromCapture = useCallback(async (withStarterTask: boolean, lifeArea?: string | null) => {
    if (!captureResult?.captureId) {
      throw new Error("That goal draft is no longer available. Please capture it again.");
    }
    let proposal = findRouteProposal(captureResult.routeProposals, "goal");
    // U17 (goal wire fix): if no goal proposal yet (route classified
    // the capture as task/idea/etc.), promote — same pattern U15 and
    // U16 use. Real backend produces a goal proposal when
    // override_category="goal".
    if (!proposal && captureResult.captureId) {
      try {
        await classifyCapture(captureResult.captureId, "goal");
        const promoted = await routeCapture(captureResult.captureId);
        const promotedGoalProposal = findRouteProposal(promoted.proposals, "goal");
        if (promotedGoalProposal) {
          proposal = promotedGoalProposal;
          setCaptureResult((previous) =>
            previous
              ? {
                  ...previous,
                  routeProposals: promoted.proposals,
                  recommendedRouteKind: promoted.recommended_route_kind,
                }
              : null
          );
        }
      } catch {
        // fall through to the original error below
      }
    }
    if (!proposal) {
      throw new Error("That goal route is no longer active. Please capture again or re-route first.");
    }
    setCaptureActionLoading(withStarterTask ? "goal-task" : "goal");
    try {
      const sourceText = captureResult.sourceText || captureResult.action || "";
      const goalDraft =
        captureResult.goalDraft
        ?? (sourceText ? await getGoalDraft(sourceText).catch(() => null) : null);
      if (!goalDraft) {
        throw new Error("That goal draft is no longer available. Please capture it again.");
      }
      // U17 (goal wire fix): the user's life-area pick from the popup
      // must override the goalDraft default. Previously `_lifeArea`
      // was prefixed unused — the user's selection silently dropped
      // and the goal landed under goalDraft.category or "general".
      const resolvedCategory = lifeArea || goalDraft.category || "general";
      const commitResult = await commitCapture(captureResult.captureId, {
        target_kind: "goal",
        confirmation: buildCommitConfirmation(proposal),
        payload: {
          title: goalDraft.title,
          description: goalDraft.description,
          category: resolvedCategory,
          target_date: buildTargetDate(goalDraft.target_weeks),
          motivation: goalDraft.motivation,
          milestones: goalDraft.milestones,
          create_starter_task: withStarterTask,
          starter_task_title: goalDraft.first_action,
          estimated_minutes: goalDraft.suggested_session_minutes,
          tags: [resolvedCategory, "goal-starter"],
        },
      });
      if (withStarterTask) {
        notifyTaskChange();
        await refresh();
      }

      showCrossSurfaceConfirmation(
        buildCaptureCommitConfirmation(commitResult.commitment, projection?.restart?.id ?? null)
      );
      completeFirstSession();
      setCaptureResult(null);
      onNavigate("goals");
    } catch (error) {
      showTransientToast(
        error instanceof Error && error.message
          ? error.message
          : "Failed to create that goal. Please try again.",
        4200
      );
      throw error;
    } finally {
      setCaptureActionLoading(null);
    }
  }, [
    captureResult,
    completeFirstSession,
    notifyTaskChange,
    onNavigate,
    refresh,
    showCrossSurfaceConfirmation,
    showTransientToast,
  ]);

  const handleCreateTaskFromCapture = useCallback(async (mode: "task" | "timebox" = "task", lifeArea?: string | null) => {
    if (!captureResult) return;
    // U11: when the route truth says schedule_block, prefer the
    // schedule_block proposal — the atomic commit path skips the
    // legacy createScheduleBlock second-write entirely.
    let scheduleBlockProposal = captureResult.captureId && mode === "timebox"
      ? findRouteProposal(captureResult.routeProposals, "schedule_block")
      : null;
    let workingRouteProposals = captureResult.routeProposals;

    // U15 (Item #3): "Place in Plan" must always commit atomically as
    // schedule_block. If the initial route was task (no schedule_block
    // proposal) but the user clicked Place in Plan, promote: re-classify
    // as schedule and re-route. The real backend will produce a
    // schedule_block proposal for any capture with concrete timing
    // (U11 promotion heuristic). This eliminates the split-brain where
    // Place in Plan first commits as task-unscheduled then writes a
    // schedule block second.
    if (
      mode === "timebox"
      && !scheduleBlockProposal
      && captureResult.captureId
    ) {
      try {
        await classifyCapture(captureResult.captureId, "schedule");
        const promoted = await routeCapture(captureResult.captureId);
        const promotedScheduleBlock = findRouteProposal(promoted.proposals, "schedule_block");
        if (promotedScheduleBlock) {
          scheduleBlockProposal = promotedScheduleBlock;
          workingRouteProposals = promoted.proposals;
          // Surface the new proposals on captureResult so a downstream
          // correction or retry sees the latest route truth too.
          setCaptureResult((previous) =>
            previous
              ? {
                  ...previous,
                  routeProposals: promoted.proposals,
                  recommendedRouteKind: promoted.recommended_route_kind,
                }
              : null
          );
        }
      } catch {
        // Promotion failed (e.g., text too vague for backend to
        // classify as schedule). Fall through to legacy fallback.
      }
    }

    const taskProposal = captureResult.captureId ? findRouteProposal(workingRouteProposals, "task") : null;
    const proposal = scheduleBlockProposal ?? taskProposal;
    if (captureResult.captureId && !proposal) {
      const error = new Error("That task route is no longer active. Please capture again or choose the suggested route.");
      showTransientToast(error.message, 4200);
      throw error;
    }
    const isAtomicScheduleBlockCommit =
      mode === "timebox" && scheduleBlockProposal !== null;
    setCaptureActionLoading(mode);
    try {
      const baseText = captureResult.sourceText || captureResult.action || "";
      const requestedTimingLabel = extractRequestedTimingLabel(baseText);
      const meta = inferTaskMeta(baseText);
      const breakdownStep = captureResult.taskBreakdown?.steps[0];
      const previewTitle = (captureResult as Record<string, unknown>).previewTitle as string | null;
      const title =
        previewTitle ||
        breakdownStep?.title ||
        captureResult.taskBreakdown?.first_action ||
        captureResult.action ||
        normalizeTaskTitle(baseText);
      const description = [
        breakdownStep?.description,
        captureResult.taskBreakdown?.summary,
        cleanMessage,
        captureResult.interpretation?.why_now ? `Why now: ${captureResult.interpretation.why_now}` : null,
      ]
        .filter(Boolean)
        .join("\n\n");

      // Honor the Intent Preview decision (U3.3):
      //   - auto-attached links default to accepted; user can dismiss
      //     via "Not this one" and the link drops
      //   - suggested links default to pending; user must explicitly
      //     accept for the link to carry into the task
      //   - any other state (pending/dismissed) → no goal_id attached
      const shouldAttachGoal =
        captureResult.relatedGoal != null && captureGoalDecision === "accepted";
      const linkedGoalTitle = shouldAttachGoal ? captureResult.relatedGoal?.title ?? null : null;
      const tags = [
        lifeArea ||
          (shouldAttachGoal ? captureResult.relatedGoal?.category : null) ||
          null,
        "today-capture",
      ].filter(Boolean) as string[];
      const taskPayload = {
        title,
        description: description || "Created from Today capture.",
        goal_id: shouldAttachGoal ? captureResult.relatedGoal?.id : undefined,
        priority: captureResult.mood === "anxious" ? 3 : meta.priority,
        energy_level: breakdownStep?.energy_level || meta.energy_level,
        estimated_minutes:
          breakdownStep?.estimated_minutes ||
          captureResult.goalDraft?.suggested_session_minutes ||
          meta.estimated_minutes,
        tags,
      };
      const scheduleIntent =
        mode === "timebox"
          ? inferCaptureScheduleIntent(
              baseText,
              captureResult.interpretation,
              taskPayload.estimated_minutes ?? meta.estimated_minutes
            )
          : null;
      let createdTaskId: string | null = null;
      let createdTaskTitle = taskPayload.title;
      let commitLandingMessage: string | null = null;
      let scheduledBlockId: string | null = null;
      let scheduledSlotLabel: string | null = null;
      let schedulePlacementMessage: string | null = null;
      // U26 — captures the backend-echoed actual block timing so the
      // cross-surface reveal can target the correct Plan date.
      let scheduleBlockCommitMeta: {
        schedule_block_start_time?: string | null;
        schedule_block_end_time?: string | null;
      } | null = null;

      if (isAtomicScheduleBlockCommit && scheduleBlockProposal && captureResult.captureId) {
        // U11 atomic path: ONE commitCapture call creates Task +
        // ScheduleBlock in a single backend transaction. No second
        // POST /api/schedule. No "left it unscheduled" lying landing
        // message. Schedule_block_id and task_id come back together.
        //
        // U26 — Bug 1 fix. Prefer the backend-parsed timing from the
        // proposal payload over local re-inference. The local
        // `inferCaptureScheduleIntent` has no weekday support, so for
        // "Tuesday at 5 PM" the slot was null and we'd fall back to
        // NOW + 1h, 30 min default. Backend's `_infer_schedule_slot`
        // already parses Tuesday + 15 min correctly; ship those
        // values straight through.
        const proposalPayload = scheduleBlockProposal.payload ?? null;
        const inferredStart = proposalPayload?.inferred_start_time ?? null;
        const inferredEnd = proposalPayload?.inferred_end_time ?? null;
        const inferredDuration = proposalPayload?.inferred_duration_minutes ?? null;
        const slot =
          inferredStart && inferredEnd
            ? null
            : scheduleIntent
            ? findPreferredScheduleSlot([], scheduleIntent)
            : null;
        const startTime = inferredStart
          ? inferredStart
          : slot
          ? slot.start.toISOString()
          : new Date(Date.now() + 60 * 60 * 1000).toISOString();
        const endTime = inferredEnd
          ? inferredEnd
          : slot
          ? slot.end.toISOString()
          : new Date(
              Date.now()
                + 60 * 60 * 1000
                + ((inferredDuration ?? taskPayload.estimated_minutes ?? 30) * 60 * 1000)
            ).toISOString();
        const commitResult = await commitCapture(captureResult.captureId, {
          target_kind: "schedule_block",
          confirmation: buildCommitConfirmation(scheduleBlockProposal),
          payload: {
            ...taskPayload,
            start_time: startTime,
            end_time: endTime,
            life_area:
              lifeArea ||
              (shouldAttachGoal ? captureResult.relatedGoal?.category ?? null : null),
            color: taskPriorityColor(taskPayload.priority),
            rationale:
              captureResult.interpretation?.why_now ||
              (scheduleIntent
                ? `Placed from Today capture for ${scheduleIntent.dayLabel}.`
                : "Placed from Today capture."),
            commitment_level: "planned",
            requested_timing_label: requestedTimingLabel,
            // Signed JS convention (positive = west of UTC) so the
            // backend can render the confirmation banner in the same
            // wall-clock the user sees on Plan.
            tz_offset_minutes: new Date().getTimezoneOffset(),
          },
        });
        createdTaskId = commitResult.commitment.task_id ?? null;
        scheduledBlockId =
          commitResult.commitment.schedule_block_id ??
          commitResult.commitment.target_id ??
          null;
        commitLandingMessage = commitResult.commitment.landing_message;
        scheduleBlockCommitMeta = {
          schedule_block_start_time:
            commitResult.commitment.schedule_block_start_time ?? null,
          schedule_block_end_time:
            commitResult.commitment.schedule_block_end_time ?? null,
        };
        if (slot) {
          scheduledSlotLabel = `${formatTime(slot.start)} - ${formatTime(slot.end)}`;
        }
        schedulePlacementMessage = buildGoalLinkAwareMessage(
          commitLandingMessage,
          linkedGoalTitle
        );
      } else if (proposal && captureResult.captureId) {
        const commitResult = await commitCapture(captureResult.captureId, {
          target_kind: "task",
          confirmation: buildCommitConfirmation(proposal),
          payload: {
            ...taskPayload,
            requested_timing_label: requestedTimingLabel,
          },
        });
        createdTaskId = commitResult.commitment.target_id;
        commitLandingMessage = commitResult.commitment.landing_message;
      } else {
        const createdTask = await createTask({
          ...taskPayload,
        });
        createdTaskId = createdTask.id;
        createdTaskTitle = createdTask.title;
      }

      // Legacy two-write path: only runs when route truth did NOT
      // produce a schedule_block proposal but heuristic schedule
      // intent is detected on a task-routed capture. U11 collapses
      // the route-truth case into the atomic branch above; this
      // remains for backwards compat with manual Place-in-Plan flows.
      if (scheduleIntent && createdTaskId && !isAtomicScheduleBlockCommit) {
        try {
          const currentSchedule = await getSchedule(scheduleIntent.targetDate).catch(
            () => ({ blocks: [], date: scheduleIntent.targetDate, total: 0 })
          );
          const slot = findPreferredScheduleSlot(currentSchedule.blocks, scheduleIntent);

          if (slot) {
            const scheduleBlock = await createScheduleBlock({
              kind: "task",
              title: taskPayload.title,
              description: taskPayload.description,
              task_id: createdTaskId,
              goal_id: taskPayload.goal_id,
              commitment_level: "planned",
              life_area: lifeArea || (shouldAttachGoal ? captureResult.relatedGoal?.category ?? undefined : undefined),
              color: taskPriorityColor(taskPayload.priority),
              rationale:
                captureResult.interpretation?.why_now ||
                `Placed from Today capture for ${scheduleIntent.dayLabel}.`,
              start_time: slot.start.toISOString(),
              end_time: slot.end.toISOString(),
            });
            scheduledBlockId = scheduleBlock.id;
            scheduledSlotLabel = `${formatTime(slot.start)} - ${formatTime(slot.end)}`;
            schedulePlacementMessage = buildGoalLinkAwareMessage(
              `Placed ${taskPayload.title} into Plan ${scheduleIntent.dayLabel} at ${scheduledSlotLabel}.`,
              linkedGoalTitle
            );
          } else if (requestedTimingLabel) {
            schedulePlacementMessage = buildGoalLinkAwareMessage(
              buildUnscheduledTaskTimingMessage(taskPayload.title, requestedTimingLabel),
              linkedGoalTitle
            );
          } else {
            schedulePlacementMessage = buildGoalLinkAwareMessage(
              `Created ${taskPayload.title} in Today, but left it unscheduled instead of pretending it landed in Plan.`,
              linkedGoalTitle
            );
          }
        } catch {
          schedulePlacementMessage = requestedTimingLabel
            ? buildGoalLinkAwareMessage(
                buildUnscheduledTaskTimingMessage(taskPayload.title, requestedTimingLabel),
                linkedGoalTitle
              )
            : buildGoalLinkAwareMessage(
                `Created ${taskPayload.title} in Today, but left it unscheduled instead of pretending it landed in Plan.`,
                linkedGoalTitle
              );
        }
      }

      notifyTaskChange();
      if (scheduledBlockId) {
        notifyScheduleChange();
      }
      await refresh();
      void loadFullQueue();
      completeFirstSession();

      if (mode === "timebox" && createdTaskId) {
        if (scheduledBlockId && schedulePlacementMessage) {
          // Stay on Today — auto-navigating to Plan kills capture
          // rhythm when the user wants to drop several captures in a
          // row. The toast still carries the schedule_block target so
          // tapping its CTA reveals + pulses on Plan; the new row in
          // QueuePeek pulses locally and shows the time chip, so the
          // user has unambiguous "it landed" feedback without losing
          // their place.
          //
          // U26 — Bug 2 fix. Prefer the backend's echoed
          // `schedule_block_start_time` for `planDate` so the reveal
          // CTA opens the correct Plan day even when local
          // `scheduleIntent` was null (e.g., "Tuesday at 5 PM" — local
          // heuristic has no weekday support). Backend already wrote
          // the row to the right day; derive the local YYYY-MM-DD
          // from the UTC ISO it echoed back.
          const committedStartIso =
            scheduleBlockCommitMeta?.schedule_block_start_time ?? null;
          const planDate = committedStartIso
            ? formatLocalDateInput(new Date(committedStartIso))
            : scheduleIntent?.targetDate
            ?? formatLocalDateInput(new Date());
          showCrossSurfaceConfirmation(
            buildScheduledTaskCaptureConfirmation(
              scheduledBlockId,
              schedulePlacementMessage,
              planDate
            )
          );
          highlightQueueItem(createdTaskId);
        } else {
          showCrossSurfaceConfirmation(
            buildTaskCaptureConfirmation(
              createdTaskId,
              schedulePlacementMessage ||
                buildGoalLinkAwareMessage(
                  commitLandingMessage ||
                    (requestedTimingLabel
                      ? buildUnscheduledTaskTimingMessage(createdTaskTitle, requestedTimingLabel)
                      : `Created ${createdTaskTitle} and placed it into Today.`),
                  linkedGoalTitle
                ),
              { requestedTimingLabel }
            )
          );
          highlightQueueItem(createdTaskId);
        }
      } else if (createdTaskId) {
        showCrossSurfaceConfirmation(
          buildTaskCaptureConfirmation(
            createdTaskId,
            buildGoalLinkAwareMessage(
              commitLandingMessage ||
                (requestedTimingLabel
                  ? buildUnscheduledTaskTimingMessage(createdTaskTitle, requestedTimingLabel)
                  : `Created ${createdTaskTitle} and placed it into Today.`),
              linkedGoalTitle
            ),
            { requestedTimingLabel }
          )
        );
        highlightQueueItem(createdTaskId);
      }

      setCaptureResult(null);
    } catch (err) {
      setCaptureActionLoading(null);
      showTransientToast(
        err instanceof Error && err.message
          ? err.message
          : "Failed to create that task. Please try again.",
        4200
      );
      throw err;
    } finally {
      setCaptureActionLoading(null);
    }
  }, [
    captureResult,
    captureGoalDecision,
    cleanMessage,
    highlightQueueItem,
    notifyScheduleChange,
    notifyTaskChange,
    onNavigate,
    openFullQueue,
    refresh,
    showCrossSurfaceConfirmation,
    showTransientToast,
    completeFirstSession,
    triggerCrossSurfaceReveal,
  ]);

  const handleStoreReferenceFromCapture = useCallback(async (lifeArea?: string | null) => {
    if (!captureResult) return;
    setCaptureActionLoading("reference");
    try {
      const sourceText = captureResult.sourceText?.trim() || "";
      const sourceUrl = extractCapturedUrl(sourceText);

      // For URLs: preview to get real title, then save with URL
      let title: string;
      let contentType: string | undefined;
      let durationMin: number | undefined;

      if (sourceUrl) {
        const preview = await previewContentCapture(sourceUrl).catch(() => null);
        title = preview?.title?.trim() || captureResult.previewTitle?.trim() || sourceUrl;
        contentType = preview?.content_type || "article";
        durationMin = preview?.duration_min ?? undefined;
      } else {
        title = captureResult.interpretation?.interpretation
          || captureResult.compression?.top_themes?.[0]
          || sourceText.slice(0, 120)
          || "Saved reference";
        contentType = "note";
      }

      // Use AI-matched goal, or find one by selected life area
      let matchingGoalId: string | undefined;
      if (captureResult.relatedGoal?.id) {
        // If user selected a different life area, find a goal in that area instead
        if (lifeArea && captureResult.relatedGoal.category !== lifeArea) {
          matchingGoalId = activeGoals.find((g) => g.category === lifeArea)?.id ?? undefined;
        } else {
          matchingGoalId = captureResult.relatedGoal.id;
        }
      } else if (lifeArea) {
        matchingGoalId = activeGoals.find((g) => g.category === lifeArea)?.id ?? undefined;
      }

      // U17 (Item #4 + audit fix #6): one atomic capture commit. The
      // backend's reference branch now creates BOTH the MemoryRecord
      // (Reflect-readable) AND the ContentQueueItem (Library-readable)
      // in one transaction, so the frontend stops issuing a separate
      // POST /api/queue. Capture truth and Library truth land
      // together with one round-trip.
      let referenceProposal = captureResult.captureId
        ? findRouteProposal(captureResult.routeProposals, "reference")
        : null;
      // Promote-and-reroute fallback when no reference proposal yet
      // (e.g., the capture was classified as something else and the
      // user clicked "Save to Library"). Same pattern as U15/U16.
      if (!referenceProposal && captureResult.captureId) {
        try {
          await classifyCapture(captureResult.captureId, "link");
          const promoted = await routeCapture(captureResult.captureId);
          referenceProposal = findRouteProposal(promoted.proposals, "reference");
          if (referenceProposal) {
            setCaptureResult((previous) =>
              previous
                ? {
                    ...previous,
                    routeProposals: promoted.proposals,
                    recommendedRouteKind: promoted.recommended_route_kind,
                  }
                : null
            );
          }
        } catch {
          // fall through to defensive queue-only write
        }
      }
      let canonicalCommitment: CaptureCommitState["commitment"] | null = null;
      if (referenceProposal && captureResult.captureId) {
        try {
          const commitResult = await commitCapture(captureResult.captureId, {
            target_kind: "reference",
            confirmation: buildCommitConfirmation(referenceProposal),
            payload: {
              title,
              url: sourceUrl ?? undefined,
              content_type: contentType,
              duration_min: durationMin,
              goal_id: matchingGoalId,
              summary: captureResult.compression?.summary ?? null,
              tags: lifeArea ? [lifeArea, "today-capture"] : ["today-capture"],
            },
          });
          canonicalCommitment = commitResult.commitment;
        } catch {
          // Defensive: if the canonical commit ever fails, drop to
          // the legacy queue-only write so the user's save still
          // lands. Capture row stays un-committed in that path.
        }
      }
      if (!canonicalCommitment) {
        // Mirror the user's full source text onto the queue item so
        // the Library card shows real body content (not just title).
        const fullBody = (captureResult?.sourceText || "").trim();
        await addToContentQueue({
          title,
          description: fullBody || undefined,
          url: sourceUrl ?? undefined,
          content_type: contentType,
          duration_min: durationMin,
          goal_id: matchingGoalId,
        });
      }
      invalidateProjectionStores();
      // BUG_EVIDENCE_PACKET_2026-05-25: capturing a reference must
      // wake up the Library list on whatever surface the user
      // navigates to next. Fire the task-change notifier so
      // BacklogScreen's subscription (added in 88b855b) refetches.
      notifyTaskChange();
      if (canonicalCommitment) {
        showCrossSurfaceConfirmation(
          buildCaptureCommitConfirmation(canonicalCommitment, projection?.restart?.id ?? null)
        );
      }
      completeFirstSession();
      setCaptureResult(null);
    } catch (err) {
      setCaptureActionLoading(null);
      throw err;
    } finally {
      setCaptureActionLoading(null);
    }
  }, [activeGoals, captureResult, completeFirstSession, notifyTaskChange, onNavigate, projection?.restart?.id, showCrossSurfaceConfirmation, showTransientToast]);

  const handleCreateDecisionFromCapture = useCallback(async () => {
    if (!captureResult) return;
    setCaptureActionLoading("decision");
    try {
      const question = captureResult.sourceText || captureResult.compression?.summary || "";
      const context = captureResult.compression?.summary || cleanMessage;
      // U12 (Q-05): when a decision route proposal exists, route the
      // commit through the canonical capture commit path so the user
      // gets the same cross-surface confirmation banner that goal and
      // task already produce. Without this, the popup vanishes with
      // no visible "I created this here" handoff (audit fix #2).
      let decisionProposal = captureResult.captureId
        ? findRouteProposal(captureResult.routeProposals, "decision")
        : null;
      // U16 (Item #4): if no decision proposal yet (heuristic
      // classifier missed the tradeoff), promote — override classify
      // to "decision" and re-route. Same pattern U15 uses for Place
      // in Plan. Real backend always produces a decision proposal
      // when override_category="decision".
      if (!decisionProposal && captureResult.captureId) {
        try {
          await classifyCapture(captureResult.captureId, "decision");
          const promoted = await routeCapture(captureResult.captureId);
          decisionProposal = findRouteProposal(promoted.proposals, "decision");
          if (decisionProposal) {
            setCaptureResult((previous) =>
              previous
                ? {
                    ...previous,
                    routeProposals: promoted.proposals,
                    recommendedRouteKind: promoted.recommended_route_kind,
                  }
                : null
            );
          }
        } catch {
          // Fall through to legacy direct createDecision.
        }
      }
      if (decisionProposal && captureResult.captureId) {
        const commitResult = await commitCapture(captureResult.captureId, {
          target_kind: "decision",
          confirmation: buildCommitConfirmation(decisionProposal),
          payload: { question, context },
        });
        invalidateProjectionStores();
        showCrossSurfaceConfirmation(
        buildCaptureCommitConfirmation(commitResult.commitment, projection?.restart?.id ?? null)
      );
        completeFirstSession();
        setCaptureResult(null);
        return;
      }
      // Legacy fallback: capture has not been routed (e.g., manual
      // Save without a route proposal) or the heuristic classifier
      // landed on capture_only without a decision proposal. The user
      // still gets the decision saved; cross-surface confirmation
      // simply isn't available without a proposal_id to bind to.
      await createDecision(question, context, []);
      invalidateProjectionStores();
      completeFirstSession();
      setCaptureResult(null);
    } catch (err) {
      setCaptureActionLoading(null);
      throw err;
    } finally {
      setCaptureActionLoading(null);
    }
  }, [captureResult, cleanMessage, completeFirstSession, showCrossSurfaceConfirmation]);

  // U13 Item #1: capture_only commit — preserve the thought without
  // promoting it to any stronger downstream surface. Maps to the
  // backend's `retained` capture status. No banner navigation; the
  // user sees the soft "Kept as capture" confirmation and the popup
  // closes.
  const handleKeepAsCaptureFromCapture = useCallback(async () => {
    if (!captureResult?.captureId) {
      // Nothing to commit; just dismiss.
      setCaptureResult(null);
      return;
    }
    const proposal = findRouteProposal(captureResult.routeProposals, "capture_only");
    if (!proposal) {
      // No capture_only proposal — drop to a soft dismiss so the user
      // is never blocked. The capture row remains in `routed` state
      // but no downstream object is created either way.
      setCaptureResult(null);
      return;
    }
    setCaptureActionLoading("capture_only");
    try {
      const commitResult = await commitCapture(captureResult.captureId, {
        target_kind: "capture_only",
        confirmation: buildCommitConfirmation(proposal),
        payload: {},
      });
      showCrossSurfaceConfirmation(
        buildCaptureCommitConfirmation(commitResult.commitment, projection?.restart?.id ?? null)
      );
      completeFirstSession();
      setCaptureResult(null);
    } catch (error) {
      showTransientToast(
        error instanceof Error && error.message
          ? error.message
          : "Could not keep that as a capture. Please try again.",
        4200
      );
      throw error;
    } finally {
      setCaptureActionLoading(null);
    }
  }, [captureResult, completeFirstSession, showCrossSurfaceConfirmation, showTransientToast]);

  const handleOpenLibraryFromCapture = useCallback(() => {
    setCaptureResult(null);
    onNavigate("library");
  }, [onNavigate]);

  const handleBuildPlanFromCapture = useCallback(async () => {
    if (!captureResult) return;
    setCaptureActionLoading("plan");
    try {
      await generateSchedule();
      notifyScheduleChange();
      showTransientToast("Today’s timetable was refreshed around what matters now.");
      completeFirstSession();
      setCaptureResult(null);
      onNavigate("schedule");
    } catch {
      showTransientToast("Failed to build today’s plan. Please try again.", 4200);
    } finally {
      setCaptureActionLoading(null);
    }
  }, [captureResult, notifyScheduleChange, onNavigate, showTransientToast, completeFirstSession]);

  const handleOpenChatFromCapture = useCallback(() => {
    if (!captureResult) return;
    onNavigate("open-chat", captureResult.sourceText || captureResult.action || "Help me think this through");
    setCaptureResult(null);
  }, [captureResult, onNavigate]);

  const handleStartRestart = useCallback(async () => {
    const restart = projection?.restart;
    if (!restart) return;
    setStartedRestartId(restart.id);
    try {
      await updateTask(restart.id, { status: "in_progress" });
      notifyTaskChange();
    } catch (actionError) {
      setStartedRestartId(null);
      showTransientToast(
        actionError instanceof Error ? actionError.message : "Could not start that restart yet."
      );
      return;
    }
    completeFirstSession();
    showTransientToast("Starting with this. One clean pass is enough progress for now.");
  }, [completeFirstSession, notifyTaskChange, projection?.restart, showTransientToast]);

  const handleRestartLater = useCallback(async () => {
    const restart = projection?.restart;
    if (!restart) return;
    setRestartActionLoading("later");
    try {
      const response = await moveRestartLater(restart.id);
      setProjection(response.today_projection);
      notifyTaskChange();
      notifyScheduleChange();
      setStartedRestartId(null);
      completeFirstSession();
      if (isQueuePlacement(response.placement.location)) {
        highlightQueueItem(response.placement.object_id ?? null);
        showTransientToast("Moved out of today. Added to the queue.", 4200, {
          ctaLabel: "Reveal item",
          onCta: () => {
            void openFullQueue({ highlightItemId: response.placement.object_id ?? null });
          },
        });
      } else if (response.placement.location === "plan") {
        highlightQueueItem(null, 0);
        showTransientToast("Moved out of today. It now lives in Plan.", 4200, {
          ctaLabel: "Open plan",
          onCta: () => onNavigate("schedule-plan"),
        });
      } else {
        highlightQueueItem(null, 0);
        showTransientToast("Moved out of today.");
      }
    } catch (actionError) {
      showTransientToast(
        actionError instanceof Error ? actionError.message : "Could not move that item later.",
        4200
      );
    } finally {
      setRestartActionLoading(null);
    }
  }, [
    completeFirstSession,
    highlightQueueItem,
    notifyScheduleChange,
    notifyTaskChange,
    onNavigate,
    openFullQueue,
    projection?.restart,
    setProjection,
    showTransientToast,
  ]);

  const handleRestartMakeSmaller = useCallback(async (smallerTitle: string) => {
    const restart = projection?.restart;
    if (!restart || !smallerTitle.trim()) return;
    setRestartActionLoading("smaller");
    try {
      const response = await makeRestartSmaller(restart.id, smallerTitle.trim());
      setProjection(response.today_projection);
      notifyTaskChange();
      notifyScheduleChange();
      setStartedRestartId(null);
      completeFirstSession();
      const createdTitle = response.created_task?.title || "Smaller step";
      if (response.placement.location === "restart") {
        highlightQueueItem(null, 0);
        showTransientToast(`${createdTitle} is now your restart. The heavier version is out of Today.`);
      } else if (isQueuePlacement(response.placement.location)) {
        highlightQueueItem(response.placement.object_id ?? null);
        await openFullQueue({ highlightItemId: response.placement.object_id ?? null });
        showTransientToast(`${createdTitle} was created and placed in the queue.`, 4200, {
          ctaLabel: "Reveal item",
          onCta: () => {
            void openFullQueue({ highlightItemId: response.placement.object_id ?? null });
          },
        });
      } else if (response.placement.location === "plan") {
        highlightQueueItem(null, 0);
        showTransientToast(`${createdTitle} was created and placed in Plan.`, 4200, {
          ctaLabel: "Open plan",
          onCta: () => onNavigate("schedule-plan"),
        });
      } else {
        highlightQueueItem(null, 0);
        showTransientToast("Created a smaller restart and cleared the heavier version from Today.");
      }
    } catch (actionError) {
      showTransientToast(
        actionError instanceof Error ? actionError.message : "Could not make that step smaller.",
        4200
      );
    } finally {
      setRestartActionLoading(null);
    }
  }, [
    completeFirstSession,
    highlightQueueItem,
    notifyScheduleChange,
    notifyTaskChange,
    onNavigate,
    openFullQueue,
    projection?.restart,
    setProjection,
    showTransientToast,
  ]);

  const handleOpenPlan = useCallback(() => {
    onNavigate("schedule-plan");
  }, [onNavigate]);

  const handleFocusCapture = useCallback(() => {
    setFocusSignal((value) => value + 1);
  }, []);

  const handleMoodCheckInComplete = useCallback(() => {
    showTransientToast("Check-in saved. Today can stay matched to your energy.");
    void refresh();
  }, [refresh, showTransientToast]);

  return (
    <div className="mx-auto max-w-xl min-w-0 px-4 py-6 pb-24">
      <header className="mb-5" data-testid="greeting">
        <p className="text-sm font-medium text-muted">Today</p>
        <h1 className="mt-1 font-serif text-3xl font-medium tracking-tight text-text">
          {getGreeting()}, {user?.display_name || "there"}
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text-light">
          {isFirstSessionPending
            ? "Capture what is on your mind. I’ll help you clarify it, choose what matters now, and help you restart later."
            : "One clear restart should be enough to get momentum back."}
        </p>
        {isFirstSessionPending ? (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="first-session-loop">
            {["Capture", "Choose", "Resume"].map((step) => (
              <span
                key={step}
                className="rounded-full border border-forest/10 bg-sage-pale/35 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-forest-med"
              >
                {step}
              </span>
            ))}
          </div>
        ) : null}
      </header>

      <AnimatePresence>
        {feedbackBanner ? (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-forest/15 bg-sage-pale/45 px-4 py-3"
            data-testid="today-feedback-banner"
          >
            <p className="text-sm font-medium text-forest">{feedbackBanner.message}</p>
            {feedbackBanner.ctaLabel && feedbackBanner.onCta ? (
              <button
                type="button"
                onClick={feedbackBanner.onCta}
                className="shrink-0 rounded-full border border-forest/15 bg-white px-3 py-1.5 text-xs font-semibold text-forest transition-colors hover:border-forest/30"
              >
                {feedbackBanner.ctaLabel}
              </button>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {error ? (
        <div className="mb-4 rounded-xl border border-amber/25 bg-amber/10 px-4 py-3 text-sm text-amber">
          Today could not load cleanly yet. Capture still works, and you can reopen Plan while we recover the projection.
        </div>
      ) : null}

      <div className="space-y-5">
        {/* 1. Capture thought */}
        <QuickCaptureBar
          onSubmit={handleCapture}
          onScreenshotRouted={(text, routeState) =>
            handleCapture(text, { prefetchedRouteState: routeState })
          }
          onScreenshotCapture={handleScreenshotCapture}
          onError={(message) => showTransientToast(message, 4200)}
          focusSignal={focusSignal}
        />

        {/* 2. Current todo (restart block) */}
        {status === "loading" && !projection ? (
          <div
            className="rounded-[28px] border border-border bg-card px-5 py-5 shadow-sm animate-pulse"
            data-testid="restart-skeleton"
          >
            <div className="h-3 w-20 rounded-full bg-secondary" />
            <div className="mt-3 h-7 w-2/3 rounded-full bg-secondary" />
            <div className="mt-3 h-4 w-full rounded-full bg-secondary" />
            <div className="mt-2 h-4 w-3/4 rounded-full bg-secondary" />
          </div>
        ) : (
          <RestartBlock
            key={startHereFocus?.id ?? "empty"}
            focus={startHereFocus}
            completedToday={projection?.consistency?.completed_today ?? 0}
            onComplete={(id) => handleCompleteFocus(id)}
            onSkip={async (id) => {
              // Beta bug 1: the focus can BE an unlinked schedule block
              // (chat-plan / manual blocks carry no task_id), so the skip
              // must branch like completeFocusItem — sending a block id to
              // the task endpoint 404'd silently and the active-now pin
              // never released.
              const result = await skipFocusItem(id, scheduleBlocks, {
                completeScheduleBlock,
                updateTask,
              });
              if (result.status === "ok") {
                notifyTaskChange();
                notifyScheduleChange();
                // No success toast — the row collapsing + next focus
                // sliding in is enough confirmation. Errors still surface.
                await refresh();
              } else {
                const err = result.error;
                const detail = err instanceof Error ? err.message : String(err);
                showTransientToast(`Could not skip: ${detail}`, 4000);
              }
            }}
          />
        )}
        {/* Phase 9-1.a (revised 2026-05-24): Today-page reminder hint.
            Two variants based on whether the user has any active goal:
              - has goals → "🌿 No plan yet for today. Open Plan →"
              - zero goals → "🌱 No goals yet. Open Goals →"
            Pure UI; no endpoint call; no inline preview. Earlier
            card-shaped CTA (with 4 AM template preview) was the wrong
            product shape and is intentionally removed.
            Gate: not loading, no focus to surface. */}
        {!(status === "loading" && !projection) && !startHereFocus && (
          <EmptyStatePlanDayCta
            onNavigate={onNavigate}
            hasActiveGoals={activeGoals.length > 0}
          />
        )}

        <AnimatePresence>
          {capturePendingText ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/25 backdrop-blur-[2px]"
              data-testid="capture-result-pending"
            >
              <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl text-center">
                <div className="mx-auto h-8 w-8 border-2 border-forest border-t-transparent rounded-full animate-spin" />
                <p className="mt-4 text-sm font-medium text-text">Thinking...</p>
                <p className="mt-1 text-xs text-muted line-clamp-2">
                  {capturePendingText.length > 60 ? `${capturePendingText.slice(0, 60)}...` : capturePendingText}
                </p>
              </div>
            </motion.div>
          ) : captureResult ? (
            <CaptureResultPopup
              data={{
                sourceText: captureResult.sourceText,
                interpretation: captureResult.interpretation,
                goalDraft: captureResult.goalDraft,
                taskBreakdown: captureResult.taskBreakdown,
                relatedGoal: captureResult.relatedGoal,
                compression: captureResult.compression,
                action: captureResult.action,
                recommendedRouteKind: captureResult.recommendedRouteKind,
                routeProposals: captureResult.routeProposals,
                captureId: captureResult.captureId,
                isError: captureResult.isError ?? false,
                agentMessage: captureResult.agentMessage,
                previewTitle: (captureResult as Record<string, unknown>).previewTitle as string | null | undefined,
              }}
              actionLoading={captureActionLoading}
              onDoToday={(lifeArea) => void handleCreateTaskFromCapture("task", lifeArea)}
              onPlaceInPlan={(lifeArea) => void handleCreateTaskFromCapture("timebox", lifeArea)}
              onSaveForLater={() => void handleStoreReferenceFromCapture()}
              onOpenDecision={() => void handleCreateDecisionFromCapture()}
              onSaveToLibrary={(lifeArea) => void handleStoreReferenceFromCapture(lifeArea)}
              onCreateGoal={(lifeArea) => void handleCreateGoalFromCapture(true, lifeArea)}
              onTalkItThrough={handleOpenChatFromCapture}
              onSaveAsCapture={() => void handleKeepAsCaptureFromCapture()}
              onDismiss={() => setCaptureResult(null)}
              onCorrectType={(type) => void handleCorrection(type)}
              goalDecision={captureGoalDecision}
              onGoalAccept={() => setCaptureGoalDecision("accepted")}
              onGoalDismiss={() => setCaptureGoalDecision("dismissed")}
            />          ) : null}
        </AnimatePresence>

        {/* Dogfood R3 bug #7 fix: Evening Recap card is now rendered
            inline on Today whenever dayPhase is "evening" (after the
            user's local 5 PM threshold per deriveDayPhase). Previously
            the only surface was EveningWrapPopup, which is gated by
            completed_today_count > 0 (the "quiet-day gate" added to
            prevent first-use trust hits on empty days). That gate is
            correct but left users with ZERO evening UX on quiet days.
            The card itself handles the 0-completions case gracefully
            (renders empty "Completed" list + the reflection prompt).
            The card and popup are complementary: card is the always-
            available anchor; popup is the proactive nudge. */}
        {!isFirstSessionPending && dayPhase === "evening" ? (
          <EveningRecapCard />
        ) : null}

        {!isFirstSessionPending ? (
          <>
            <div ref={queueSectionRef}>
              <QueuePeek
                items={projection?.queue_peek ?? []}
                fullQueueItems={fullQueueItems}
                isExpanded={fullQueueExpanded}
                isLoading={fullQueueLoading}
                onToggleExpanded={toggleFullQueue}
                highlightedItemId={highlightedQueueItemId}
                restartActive={
                  Boolean(projection?.restart) || Boolean(startHereFocus)
                }
                scheduleBlocks={scheduleBlocks}
                onRevealBlock={(block) => {
                  showCrossSurfaceConfirmation(
                    buildScheduledTaskCaptureConfirmation(
                      block.id,
                      `Opening ${sanitizeSurfaceTitle(block.title)} on Plan.`,
                      formatLocalDateInput(parseUTCishDate(block.start_time)),
                    ),
                  );
                  // "schedule" (not "schedule-plan") — switches to the
                  // Plan tab WITHOUT bumping scheduleAutoOpenPlanToken.
                  // The chip is a reveal-only handoff; auto-firing AI
                  // Time Box on top of the user just trying to look at
                  // a placed block is the wrong intent.
                  onNavigate("schedule");
                  triggerCrossSurfaceReveal();
                }}
                goalCategoryById={Object.fromEntries(
                  Array.from(activeGoalMap.entries()).map(([goalId, value]) => [goalId, value.category])
                )}
                goalCategoryHints={activeGoals.map((goal) => ({
                  title: goal.title,
                  category: goal.category ?? null,
                }))}
                lifeAllocation={lifeAllocation}
                onTaskCreated={() => {
                  notifyTaskChange();
                  void loadFullQueue();
                }}
                onPromoteToFocus={handlePromoteToFocus}
              />
            </div>

            {showBrief ? (
              <MorningBriefPopup
                onDismiss={() => setShowBrief(false)}
                onDetails={(summary) => {
                  setShowBrief(false);
                  if (summary) {
                    setCarryForwardSummary(summary);
                  }
                  onNavigate("schedule-plan");
                }}
                onReviewToday={(summary) => {
                  setShowBrief(false);
                  setCarryForwardSummary(summary);
                  void openFullQueue();
                  showTransientToast("Reviewing what is still waiting for Today.");
                }}
                displayName={user?.display_name ?? null}
                currentStreak={projection?.consistency?.current_streak ?? 0}
              />
            ) : null}

            {/* Today gets one job: action + capture. Removed in §6.3:
                - ConsistencyPulse (Morning Pulse) — moved to Reflect's
                  "How are the rhythms?" section.
                - DeferredModulesRail wrapper (More Support · Suggestions N).
                - ExploreCard (AI Suggestion 'Do today / Save to Library /
                  Hide' card) — moved to Reflect's "What's worth
                  considering?" section.
                The Need help card stays — it's not a suggestion surface,
                just a static chat affordance. */}

            <div className="rounded-2xl border border-border bg-bg p-4 shadow-sm" data-testid="today-help-card">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
                Need help?
              </p>
              <p className="mt-2 text-sm leading-relaxed text-text">
                Chat stays available, but it should help after Today shows one real move first.
              </p>
              <button
                type="button"
                onClick={() => onNavigate("open-chat", "Help me choose the lightest workable next step.")}
                className="mt-3 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-text-light transition-colors hover:border-forest/25 hover:text-text"
              >
                Open chat
              </button>
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-border bg-bg p-4 shadow-sm" data-testid="today-help-card">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
              Need help?
            </p>
            <p className="mt-2 text-sm leading-relaxed text-text">
              If the restart still feels too big, ask for the lightest workable next step.
            </p>
            <button
              type="button"
              onClick={() => onNavigate("open-chat", "Help me choose the lightest workable next step.")}
              className="mt-3 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-text-light transition-colors hover:border-forest/25 hover:text-text"
            >
              Open chat
            </button>
          </div>
        )}
      </div>

      {showEveningPopup && (
        <EveningWrapPopup
          onDismiss={() => setShowEveningPopup(false)}
          onOpenPlan={() => {
            setShowEveningPopup(false);
            onNavigate("schedule-plan");
          }}
          displayName={user?.display_name ?? null}
          completedToday={projection?.consistency?.completed_today ?? 0}
          prefetchedBrief={eveningBriefCache}
        />
      )}
    </div>
  );
}
